import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHasPermission } from "@/hooks/use-permissions";
import { DateTime } from "luxon";
import {
  Ban,
  Building2,
  CalendarCheck2,
  CalendarDays,
  CalendarOff,
  CalendarRange,
  Clock,
  Columns2,
  Droplet,
  Eye,
  EyeOff,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronsUpDown,
  FilterX,
  LayoutGrid,
  ListChecks,
  Minus,
  Plus,
  PanelLeft,
  RotateCcw,
  Rows3,
  Ruler,
  SlidersHorizontal,
  Stethoscope,
  Sunrise,
  Sunset,
} from "lucide-react";
import {
  addDays,
  addMonths,
  formatDayHeading,
  formatMonthYear,
  formatWeekRange,
  getMonthMatrix,
  groupEventsByDate,
  sortEventsByStart,
  toDateKey,
  VIEW_TYPES,
  isCancelledCalendarEvent,
  buildEventColorTheme,
  isNewPatient,
  isToday,
} from "./calendar-utils";
import { cn, ALT_KEY_LABEL } from "@/lib/utils";
import {
  getDoctorColorMap,
  getEventsForMonth,
  getEventsForDayRange,
  getStandaloneCalendarOptions,
  invalidateCalendarsCache,
} from "./calendar-data";
import {
  buildDayViewColumns,
  canDropOnColumn,
  canDropOnSlot,
  directEntryRangeFromMinute,
  filterCalendarEvents,
  isStandaloneCalendarEvent,
  minutesToHhmm,
  resolveSavedCalendarIds,
} from "@/lib/calendars";
import { eventGridPositionForAxis, getCalendarTimeAxis } from "./calendar-time-axis";
import { useCalendarZoomPan } from "./use-calendar-zoom-pan";
import { computeFitZoomPercent } from "./calendar-fit-zoom";
import { useTimeOffBlocks } from "./calendar-blocks";
import DayView from "./day-view";
import WeekView from "./week-view";
import MonthView from "./month-view";
import { SessionOverrideDialog } from "./sessions/override-dialog";
import { CalendarEntryFormSheet } from "./day-view/calendar-entry-form-sheet";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Calendar as DatePickerCalendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { FacetFilter, QuickFacetRow, SegmentedToggle } from "./facet-filter";
import { SavedFiltersBar } from "./saved-filters-bar";
import { Spinner } from "@/components/ui/spinner";
import { fetchTenants } from "@/lib/api/tenants";
import { filterClinicsForSurface } from "@/lib/clinic-visibility";
import { fetchDoctors } from "@/lib/api/users";
import { resolveTenantDisplayName } from "@/lib/tenant-display";
import {
  DndContext,
  DragOverlay,
  useSensor,
  useSensors,
  PointerSensor,
  pointerWithin,
} from "@dnd-kit/core";
import {
  SNAP_MINUTES,
  canMutateAppointmentEvent,
  clampStartMinutes,
  closestTimeGeometryElement,
  measureColumnGeometry,
  snapMinutes,
} from "./calendar-drag-geometry";
import { getWeekDayWindow } from "./week-day-window";
import {
  CalendarMutationsProvider,
  reconcilePendingOverrides,
  useCalendarAppointmentMutations,
} from "./calendar-mutations";
import {
  getAppointmentFilters,
  setAppointmentFilters,
  subscribeAppointmentFilters,
} from "@/lib/stores/appointment-filters-store";
import {
  getSavedCalendarFilters,
  addSavedCalendarFilter,
  removeSavedCalendarFilter,
  renameSavedCalendarFilter,
  reorderSavedCalendarFilters,
  subscribeSavedCalendarFilters,
} from "@/lib/stores/saved-calendar-filters-store";
import {
  getUserPreferences,
  setUserPreferences,
  subscribeUserPreferences,
} from "@/lib/stores/user-preference-store";
import { idsSortedByName, moveIdToIndex, orderByIds } from "@/lib/entity-order";
import {
  getAppointmentRefreshVersion,
  subscribeAppointmentRefresh,
} from "@/lib/realtime/appointment-realtime-bridge";
import {
  consumePendingOpenCalendarAppointment,
  subscribeOpenCalendarAppointment,
} from "@/lib/calendar/calendar-navigation";
import { toast } from "sonner";

import { formatTime, startOfDay, endOfDay, startOfMonth, endOfMonth } from "./calendar-utils";

const VIEW_CONFIG = [
  { id: VIEW_TYPES.DAY, label: "Day" },
  { id: VIEW_TYPES.WEEK, label: "Week" },
  { id: VIEW_TYPES.MONTH, label: "Month" },
];
const DAY_VIEW_GROUP_BY_MODES = [
  {
    id: "tenant",
    label: "Clinic",
    icon: Building2,
    title: "Split the day into side-by-side location columns",
  },
  {
    id: "doctor",
    label: "Doctor",
    icon: Stethoscope,
    title: "Split the day into side-by-side doctor columns",
  },
];
const ZOOM_OPTIONS = [100, 150, 200, 300, 400];
// Custom-value bounds (mirror the preference store's clamps).
const ZOOM_MIN_PERCENT = 50;
const ZOOM_MAX_PERCENT = 1200;
// Month "cell height" zoom is a separate value with a tighter ceiling (mirrors the store).
const MONTH_ZOOM_MIN_PERCENT = 50;
const MONTH_ZOOM_MAX_PERCENT = 800;
// Horizontal ("column width") zoom for day & week (mirrors the store).
const WIDTH_ZOOM_MIN_PERCENT = 50;
const WIDTH_ZOOM_MAX_PERCENT = 800;
// Grid-width quick-picks. Unlike the vertical zoom these go BELOW 100%: the grid
// then renders narrower than the pane instead of filling it edge to edge.
const WIDTH_ZOOM_OPTIONS = [50, 75, 100, 150, 200];
// Day-view "session" jump targets: the toolbar AM/PM buttons scroll the grid to
// the start of the morning (08:00) and afternoon (13:00) sessions.
const AM_SESSION_HOUR = 8;
const PM_SESSION_HOUR = 13;
// Open-slot opacity quick-picks + custom bounds (mirror the store).
const SLOT_OPACITY_OPTIONS = [25, 50, 75, 100];
const SLOT_OPACITY_MIN_PERCENT = 10;
const SLOT_OPACITY_MAX_PERCENT = 100;
const COLUMN_WIDTH_MIN_PX = 160;
const COLUMN_WIDTH_MAX_PX = 640;

// Shared guard for the calendar's global key shortcuts (Alt+0 reset, Alt+Shift+0
// fit, Alt+1–9 presets): ignore keys while the user is typing (on macOS ⌥+digit
// is character input, e.g. ⌥1 → "¡"), and only act when focus is inside the
// calendar section (or nowhere in particular).
function calendarKeyEventAllowed(event, sectionEl) {
  const target = event.target;
  const tag = target?.tagName;
  if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || target?.isContentEditable) {
    return false;
  }
  const active = document.activeElement;
  return !active || active === document.body || (sectionEl && sectionEl.contains(active));
}

// Segment sets for the Display popover's compact toggles.
const TIME_FORMAT_SEGMENTS = [
  { id: "24h", label: "24h" },
  { id: "12h", label: "12h" },
];
const SLOTS_SEGMENTS = [
  { id: "free", label: "Free", icon: CalendarCheck2, title: "Available slots only" },
  { id: "all", label: "All", icon: CalendarRange, title: "Include unavailable slots" },
  { id: "hide", label: "Hide", icon: CalendarOff, title: "Hide slots" },
];
const CANCELLED_SEGMENTS = [
  { id: "show", label: "Show", icon: Eye, title: "Show cancelled appointments" },
  { id: "hide", label: "Hide", icon: EyeOff, title: "Hide cancelled appointments" },
];
// How a facet filter (Clinics / Doctors) is presented on the main filter line:
// the compact searchable dropdown, or an inline row of quick-toggle checkboxes.
const FILTER_STYLE_SEGMENTS = [
  { id: "dropdown", label: "Dropdown", icon: ChevronsUpDown, title: "Compact searchable dropdown" },
  { id: "checkboxes", label: "Checkboxes", icon: ListChecks, title: "Inline row of quick checkboxes" },
];
// Fallback swatch hues for the doctor quick-filter chips when a doctor has no
// Plato calendar color. These are the tailwind-600 hexes of calendar-data's
// EVENT_COLORS in the same order, picked with the same hash, so a doctor's chip
// hue matches the deterministic color used elsewhere for that doctor.
const DOCTOR_SWATCH_PALETTE = [
  "#2563eb", // blue-600
  "#4f46e5", // indigo-600
  "#059669", // emerald-600
  "#9333ea", // purple-600
  "#db2777", // pink-600
  "#ea580c", // orange-600
  "#0891b2", // cyan-600
  "#e11d48", // rose-600
  "#d97706", // amber-600
];
function doctorSwatchFallback(id) {
  const value = String(id || "");
  let hash = 0;
  for (let i = 0; i < value.length; i += 1) {
    hash = (hash << 5) - hash + value.charCodeAt(i);
    hash |= 0;
  }
  return DOCTOR_SWATCH_PALETTE[Math.abs(hash) % DOCTOR_SWATCH_PALETTE.length];
}
const COLUMN_WIDTH_SEGMENTS = [
  { id: "dynamic", label: "Dynamic", title: "Columns share the available width equally" },
  { id: "comfortable", label: "Comfortable", title: "Each column ≥ 240px; scrolls when needed" },
  { id: "spacious", label: "Spacious", title: "Each column ≥ 320px; scrolls when needed" },
  { id: "custom", label: "Custom", title: "Set an exact column width in pixels" },
];
const ZOOM_SEGMENTS = [
  ...ZOOM_OPTIONS.map((z) => ({ id: z, label: `${z}%` })),
  { id: "custom", label: "Custom", title: "Set an exact hour-row height" },
];
const WIDTH_ZOOM_SEGMENTS = [
  ...WIDTH_ZOOM_OPTIONS.map((z) => ({
    id: z,
    label: `${z}%`,
    title:
      z < 100
        ? `Columns fill ${z}% of the pane`
        : z > 100
          ? `Columns overflow to ${z}% and scroll`
          : "Columns fill the pane",
  })),
  { id: "custom", label: "Custom", title: "Set an exact grid width" },
];
const SLOT_OPACITY_SEGMENTS = [
  ...SLOT_OPACITY_OPTIONS.map((o) => ({ id: o, label: `${o}%` })),
  { id: "custom", label: "Custom", title: "Set an exact slot opacity" },
];
// "Min interval" tick spacing — divisors of 60 so tick lines stay hour-aligned.
const TICK_MINUTES_SEGMENTS = [5, 10, 15, 30, 60].map((m) => ({
  id: m,
  label: `${m}m`,
  title: `Gridline every ${m} minutes`,
}));
// Day-view "session rail": show/hide + accent-vs-neutral coloring + width range.
const SESSION_RAIL_SEGMENTS = [
  { id: "on", label: "On", title: "Show the background session rail" },
  { id: "off", label: "Off", title: "Hide the background session rail" },
];
const RAIL_COLOR_MODE_SEGMENTS = [
  { id: "accent", label: "Accent", title: "Each doctor / clinic in its own muted hue" },
  { id: "neutral", label: "Neutral", title: "A single calm gray for every band" },
];
const RAIL_WIDTH_MIN_PX = 14;
const RAIL_WIDTH_MAX_PX = 40;
const RAIL_MARGIN_MIN_PX = 0;
const RAIL_MARGIN_MAX_PX = 8;
// Day-window bounds (whole hours). The end stays strictly after the start.
const DAY_START_HOUR_MIN = 0;
const DAY_END_HOUR_MAX = 24;

// A titled group of settings inside the Display popover. Sections are separated
// by dividers (the parent's `divide-y`); the caption gives the panel scannable
// hierarchy, and — for the view-specific group — names the active view so the
// old per-row "Day view" / "Day & week" hints are no longer needed.
function SettingSection({ title, children }) {
  return (
    <section className="space-y-2.5 px-3 py-3">
      {title ? (
        <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          {title}
        </p>
      ) : null}
      <div className="space-y-2">{children}</div>
    </section>
  );
}

// A single setting inside a SettingSection. `layout="inline"` puts an icon+label
// on the left and the control right-aligned (for compact binary/ternary toggles);
// `layout="stack"` drops the control full-width beneath the label (for the wide
// preset rows and range steppers, so nothing overflows the popover).
function SettingRow({ icon: Icon, label, layout = "inline", children }) {
  const head = (
    <div className="flex min-w-0 items-center gap-2">
      {Icon ? <Icon className="size-3.5 shrink-0 text-muted-foreground" /> : null}
      <p className="truncate text-[13px] font-medium text-foreground">{label}</p>
    </div>
  );
  if (layout === "stack") {
    return (
      <div className="space-y-1.5">
        {head}
        {children}
      </div>
    );
  }
  return (
    <div className="flex items-center justify-between gap-3">
      {head}
      <div className="shrink-0">{children}</div>
    </div>
  );
}

// A compact −[ number ]+ stepper for custom sizes. Keeps a local text buffer so
// the field is freely typeable; commits (clamped) on blur, Enter, or a step.
function StepperInput({ value, onChange, min, max, step = 10, suffix, ariaLabel }) {
  const [text, setText] = useState(String(value));
  useEffect(() => {
    setText(String(value));
  }, [value]);
  const clamp = (n) => Math.min(max, Math.max(min, Math.round(n)));
  const commit = () => {
    const n = Number(text);
    if (text.trim() !== "" && Number.isFinite(n)) {
      onChange(clamp(n));
    } else {
      setText(String(value));
    }
  };
  const stepBy = (delta) => onChange(clamp(value + delta));
  return (
    <div className="inline-flex h-8 items-center overflow-hidden rounded-lg border border-border bg-card">
      <button
        type="button"
        aria-label="Decrease"
        onClick={() => stepBy(-step)}
        disabled={value <= min}
        className="flex h-full w-7 items-center justify-center text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:pointer-events-none disabled:opacity-40"
      >
        <Minus className="size-3.5" />
      </button>
      <input
        type="text"
        inputMode="numeric"
        aria-label={ariaLabel}
        value={text}
        onChange={(event) => setText(event.target.value.replace(/[^0-9]/g, ""))}
        onBlur={commit}
        onKeyDown={(event) => {
          if (event.key === "Enter") event.currentTarget.blur();
        }}
        className="h-full w-11 border-x border-border bg-transparent text-center text-[12px] font-medium tabular-nums text-foreground outline-none"
      />
      {suffix ? (
        <span className="px-1.5 text-[11px] text-muted-foreground">{suffix}</span>
      ) : null}
      <button
        type="button"
        aria-label="Increase"
        onClick={() => stepBy(step)}
        disabled={value >= max}
        className="flex h-full w-7 items-center justify-center text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:pointer-events-none disabled:opacity-40"
      >
        <Plus className="size-3.5" />
      </button>
    </div>
  );
}

function areIdArraysEqual(left, right) {
  if (left === right) {
    return true;
  }
  if (!Array.isArray(left) || !Array.isArray(right) || left.length !== right.length) {
    return false;
  }
  for (let index = 0; index < left.length; index += 1) {
    if (left[index] !== right[index]) {
      return false;
    }
  }
  return true;
}

function Calendar() {
  // RBAC: calendar:view renders everything read-only — drag/resize sensors detach below and
  // the mutation hub (applyAppointmentUpdate) refuses amendments as the backstop.
  const canManageCalendar = useHasPermission("calendar", "manage");
  // Session-block overrides live under the scheduling module (same as Slots/Sessions pages).
  const canManageScheduling = useHasPermission("scheduling", "manage");
  // Day-view quick action: { doctorId, date: "YYYY-MM-DD" } opens the per-day
  // session override dialog pre-filtered to that doctor + date.
  const [sessionOverrideTarget, setSessionOverrideTarget] = useState(null);
  // "New entry" sheet for a standalone-calendar column: { calendarId, calendarName,
  // colorHex, localOnly, date, startTime, endTime } while open, null otherwise.
  const [entryDraft, setEntryDraft] = useState(null);
  const initialFilters = getAppointmentFilters();
  const hasInitializedTenantSelectionRef = useRef(false);
  const hasInitializedDoctorSelectionRef = useRef(false);
  const hasInitializedCalendarSelectionRef = useRef(false);
  const hasInitiallyScrolledToNowRef = useRef(false);
  const savedScrollRatioRef = useRef(null);
  // Zoom/pan plumbing. The listeners attach to wrapperRef; each view registers its own
  // scroll viewport into activeViewportRef; the wheel handler stashes a cursor anchor in
  // zoomAnchorRef which the views' zoom-compensation effects consume. resetSignal bumps to
  // re-trigger each view's scroll-to-now/top after a `0`-key / button reset.
  const sectionRef = useRef(null);
  const wrapperRef = useRef(null);
  const activeViewportRef = useRef(null);
  const zoomAnchorRef = useRef(null);
  const [resetSignal, setResetSignal] = useState(0);
  const [activeView, setActiveView] = useState(
    () => getUserPreferences().calendarView
  );
  const [selectedDate, setSelectedDate] = useState(() => DateTime.now());
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [pickerMonth, setPickerMonth] = useState(null);
  const datePickerWheelCleanupRef = useRef(null);
  const [events, setEvents] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  // Context key (view|dates|filters) of the events currently in state — a refetch of the
  // same key revalidates silently instead of raising the blocking loading overlay.
  const loadedEventsContextRef = useRef("");
  const [isDay12HourFormat, setIsDay12HourFormat] = useState(
    () => getUserPreferences().calendarUse12HourFormat
  );
  const [isIncludeSlots, setIsIncludeSlots] = useState(() => getUserPreferences().calendarIncludeSlots);
  const [isIncludeNotAvailableSlots, setIsIncludeNotAvailableSlots] = useState(
    () => getUserPreferences().calendarIncludeNotAvailableSlots
  );
  const [isShowCancelled, setIsShowCancelled] = useState(() => getUserPreferences().calendarShowCancelled);
  // Present each facet as an inline row of quick checkboxes (true) instead of the
  // compact searchable dropdown (false). Toggled per facet in the Display popover.
  const [clinicsQuickFilter, setClinicsQuickFilter] = useState(
    () => getUserPreferences().calendarClinicsQuickFilter
  );
  const [doctorsQuickFilter, setDoctorsQuickFilter] = useState(
    () => getUserPreferences().calendarDoctorsQuickFilter
  );
  // The user's own chip order for each facet. Also drives the day-view column
  // order, since the columns are a filtered view of the same option arrays.
  const [calendarClinicOrder, setCalendarClinicOrder] = useState(
    () => getUserPreferences().calendarClinicOrder
  );
  const [calendarDoctorOrder, setCalendarDoctorOrder] = useState(
    () => getUserPreferences().calendarDoctorOrder
  );
  const [calendarZoomPercent, setCalendarZoomPercent] = useState(
    () => getUserPreferences().calendarZoomPercent
  );
  const [calendarMonthZoomPercent, setCalendarMonthZoomPercent] = useState(
    () => getUserPreferences().calendarMonthZoomPercent
  );
  const [calendarWidthZoomPercent, setCalendarWidthZoomPercent] = useState(
    () => getUserPreferences().calendarWidthZoomPercent
  );
  const [calendarSlotOpacityPercent, setCalendarSlotOpacityPercent] = useState(
    () => getUserPreferences().calendarSlotOpacityPercent
  );
  // Whether the slot-opacity custom input is showing (mirrors the zoom custom toggle).
  const [slotOpacityCustomOpen, setSlotOpacityCustomOpen] = useState(
    () => !SLOT_OPACITY_OPTIONS.includes(getUserPreferences().calendarSlotOpacityPercent)
  );
  const [calendarColumnWidthMode, setCalendarColumnWidthMode] = useState(
    () => getUserPreferences().calendarColumnWidthMode
  );
  const [calendarColumnWidthCustomPx, setCalendarColumnWidthCustomPx] = useState(
    () => getUserPreferences().calendarColumnWidthCustomPx
  );
  // Whether the Hour-row-height custom input is showing. The zoom value itself is
  // a plain number; this just tracks intent so the input stays open even when the
  // typed value happens to equal a preset.
  const [zoomCustomOpen, setZoomCustomOpen] = useState(
    () => !ZOOM_OPTIONS.includes(getUserPreferences().calendarZoomPercent)
  );
  // Same intent-tracking for the Grid-width custom input (see zoomCustomOpen).
  const [widthZoomCustomOpen, setWidthZoomCustomOpen] = useState(
    () => !WIDTH_ZOOM_OPTIONS.includes(getUserPreferences().calendarWidthZoomPercent)
  );
  const [calendarDayViewGroupBy, setCalendarDayViewGroupBy] = useState(
    () => getUserPreferences().calendarDayViewGroupBy
  );
  const [calendarDaySplitEnabled, setCalendarDaySplitEnabled] = useState(
    () => getUserPreferences().calendarDaySplitEnabled
  );
  // The second day pane's date. Null = "not explicitly chosen"; the pane then
  // defaults to the day after the selected date (and follows it) until the user
  // navigates the pane directly.
  const [daySplitDate, setDaySplitDate] = useState(null);
  const [calendarDayStartHour, setCalendarDayStartHour] = useState(
    () => getUserPreferences().calendarDayStartHour
  );
  const [calendarDayEndHour, setCalendarDayEndHour] = useState(
    () => getUserPreferences().calendarDayEndHour
  );
  const [calendarTickMinutes, setCalendarTickMinutes] = useState(
    () => getUserPreferences().calendarTickMinutes
  );
  const [calendarShowSessionRail, setCalendarShowSessionRail] = useState(
    () => getUserPreferences().calendarShowSessionRail
  );
  const [calendarRailWidthPx, setCalendarRailWidthPx] = useState(
    () => getUserPreferences().calendarRailWidthPx
  );
  const [calendarRailColorMode, setCalendarRailColorMode] = useState(
    () => getUserPreferences().calendarRailColorMode
  );
  const [calendarRailMarginPx, setCalendarRailMarginPx] = useState(
    () => getUserPreferences().calendarRailMarginPx
  );
  const [hideSunday, setHideSunday] = useState(
    () => getUserPreferences().calendarHideSunday
  );
  const [tenantOptions, setTenantOptions] = useState([]);
  const [selectedTenantIds, setSelectedTenantIds] = useState(() =>
    Array.isArray(initialFilters.tenantIds) ? initialFilters.tenantIds : [],
  );
  const [doctorOptions, setDoctorOptions] = useState([]);
  const [selectedDoctorIds, setSelectedDoctorIds] = useState(() =>
    Array.isArray(initialFilters.doctorIds) ? initialFilters.doctorIds : [],
  );
  // doctorId → hex of each doctor's calendar color, used to tint the doctor
  // quick-filter chips. Empty until the (cached) calendars fetch resolves.
  const [doctorColorById, setDoctorColorById] = useState(() => new Map());
  // Standalone admin calendars (no doctor, no clinic): the "Calendars" facet and one
  // day-view column per selected calendar. A stored `null` means "all" (blobs and
  // presets that predate the facet); the loader resolves it against the options.
  // Declared here — above applySavedFilter and the Alt+1–9 effect — because both
  // read these bindings (TDZ crash otherwise). The ref mirror lets store/refresh
  // subscriptions with [] deps reconcile against the latest options.
  const [standaloneCalendarOptions, setStandaloneCalendarOptions] = useState([]);
  const [selectedCalendarIds, setSelectedCalendarIds] = useState(() =>
    Array.isArray(initialFilters.calendarIds) ? initialFilters.calendarIds : [],
  );
  const standaloneCalendarOptionsRef = useRef(standaloneCalendarOptions);
  standaloneCalendarOptionsRef.current = standaloneCalendarOptions;
  const [dayViewFocusedEventId, setDayViewFocusedEventId] = useState(null);
  // Deep link from elsewhere in the app ("show this appointment on the calendar"). The
  // target day's events aren't fetched yet when we arrive, so the request parks here as
  // { appointmentId, dateKey, relaxed } until the event actually renders; then it becomes
  // a highlight ({ eventId, dateKey }) plus a focusSignal bump that scrolls to the card.
  const [pendingFocus, setPendingFocus] = useState(null);
  const [appointmentHighlight, setAppointmentHighlight] = useState(null);
  const [focusSignal, setFocusSignal] = useState(0);
  const [appointmentRefreshVersion, setAppointmentRefreshVersion] = useState(() =>
    getAppointmentRefreshVersion()
  );
  // Saved filter presets (named tenant + doctor snapshots) shown in the bar under the
  // header. A ref mirror keeps the 1–9 key handler reading the latest list without re-binding.
  const [savedFilters, setSavedFilters] = useState(() => getSavedCalendarFilters());
  const savedFiltersRef = useRef(savedFilters);
  savedFiltersRef.current = savedFilters;
  const [savedFiltersExpanded, setSavedFiltersExpanded] = useState(
    () => getUserPreferences().calendarFilterBadgesExpanded
  );

  // Drag and drop states. dragStateRef carries the per-gesture geometry
  // (grab offset within the card, duration, card size) captured at drag start;
  // dragPreview is the live snapped time label shown on the DragOverlay ghost.
  const [activeDragEvent, setActiveDragEvent] = useState(null);
  const [dragPreview, setDragPreview] = useState(null);
  const dragStateRef = useRef(null);

  // Single commit path (optimistic apply + undo toast + reason prompt) shared
  // by drag-move here and resize/editor saves in CalendarEventItem (via context).
  const {
    applyAppointmentUpdate,
    requestAppointmentConfirmation,
    pendingOverridesRef,
    reasonDialogElement,
    confirmationDialogElement,
  } = useCalendarAppointmentMutations({
    setEvents,
  });
  const calendarMutationsValue = useMemo(
    () => ({ applyAppointmentUpdate, requestAppointmentConfirmation, canManage: canManageCalendar }),
    [applyAppointmentUpdate, requestAppointmentConfirmation, canManageCalendar],
  );

  // Distance-based activation: the drag arms as soon as the pointer travels a
  // few px, so grabbing a card feels instant. Plain clicks and double-clicks
  // still work — they don't move the pointer past the threshold. (The previous
  // 250ms hold delay made every drag feel laggy.)
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    })
  );

  // The run of days the week view renders: the anchor week plus a fixed week
  // either side. See week-day-window.js — this is why the week view fetches a day
  // RANGE rather than a calendar week. Deliberately NOT a function of the zoom:
  // the day set (and its fetch) must hold still while the columns resize, or
  // every wheel notch would remount columns and flash the loading overlay.
  const weekDayWindow = useMemo(
    () => getWeekDayWindow(selectedDate, { hideSunday }),
    [selectedDate, hideSunday],
  );
  // Stable identity for the rendered span — the same for every day of a week, so
  // clicking another day of the visible week revalidates silently.
  const weekRangeKey = useMemo(() => {
    const days = weekDayWindow.days;
    return `${days[0]?.toISODate?.() || ""}..${days[days.length - 1]?.toISODate?.() || ""}`;
  }, [weekDayWindow]);

  // Resolved date of the second day pane ("2 days" split, day view only).
  const effectiveDaySplitDate = useMemo(() => {
    if (activeView !== VIEW_TYPES.DAY || !calendarDaySplitEnabled) {
      return null;
    }
    return daySplitDate ?? addDays(selectedDate, 1);
  }, [activeView, calendarDaySplitEnabled, daySplitDate, selectedDate]);

  useEffect(() => {
    let isMounted = true;

    const loadEvents = async () => {
      // Stale-while-revalidate: the blocking "Loading events..." overlay only makes sense
      // when the on-screen events do not match the requested context (view/date/filter
      // change, or nothing loaded yet). A refetch of the SAME context — Activity re-show
      // after a tab/profile switch, or a WS appointmentRefreshVersion bump — must revalidate
      // silently in the background: the user keeps their grid and scroll position while
      // fresh data swaps in underneath.
      const contextKey = [
        activeView,
        // Week view is keyed on its rendered SPAN, not the selected day: every
        // day of a week resolves to the same fetch, so moving the selection
        // inside the visible week never shows the loading overlay.
        activeView === VIEW_TYPES.WEEK ? weekRangeKey : selectedDate?.toISODate?.() || "",
        effectiveDaySplitDate?.toISODate?.() || "",
        isIncludeSlots,
        isIncludeNotAvailableSlots,
      ].join("|");
      const isSilentRefresh = loadedEventsContextRef.current === contextKey;
      if (!isSilentRefresh) setIsLoading(true);
      try {
        let nextEvents = [];
        if (activeView === VIEW_TYPES.DAY) {
          // The split pane can sit in a different month than the selected day —
          // fetch both months and merge (dedup by id; a boundary-spanning event
          // can come back from both fetches).
          const monthFetches = [getEventsForMonth(selectedDate)];
          if (effectiveDaySplitDate && !effectiveDaySplitDate.hasSame(selectedDate, "month")) {
            monthFetches.push(getEventsForMonth(effectiveDaySplitDate));
          }
          const [primaryMonthEvents, splitMonthEvents] = await Promise.all(monthFetches);
          nextEvents = primaryMonthEvents;
          if (splitMonthEvents) {
            const seenIds = new Set(primaryMonthEvents.map((event) => event.id));
            nextEvents = primaryMonthEvents.concat(
              splitMonthEvents.filter((event) => !seenIds.has(event.id)),
            );
          }
        } else if (activeView === VIEW_TYPES.WEEK) {
          nextEvents = await getEventsForDayRange(
            weekDayWindow.days[0],
            weekDayWindow.days[weekDayWindow.days.length - 1],
          );
        } else {
          // Bookings only — the month grid drops slots (see MonthView), so they
          // are not worth fetching.
          nextEvents = await getEventsForMonth(selectedDate, { includeSlots: false });
        }

        if (isMounted) {
          // Re-apply in-flight optimistic updates so a WS-triggered refetch
          // racing an appointment mutation doesn't snap the card back.
          setEvents(sortEventsByStart(reconcilePendingOverrides(nextEvents, pendingOverridesRef)));
          loadedEventsContextRef.current = contextKey;
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadEvents();
    return () => {
      isMounted = false;
    };
  }, [
    activeView,
    selectedDate,
    effectiveDaySplitDate,
    isIncludeSlots,
    isIncludeNotAvailableSlots,
    appointmentRefreshVersion,
  ]);

  useEffect(() => {
    return subscribeUserPreferences((prefs) => {
      setCalendarZoomPercent(prefs.calendarZoomPercent);
      setCalendarMonthZoomPercent(prefs.calendarMonthZoomPercent);
      setCalendarWidthZoomPercent(prefs.calendarWidthZoomPercent);
      setCalendarSlotOpacityPercent(prefs.calendarSlotOpacityPercent);
      setActiveView(prefs.calendarView);
      setIsDay12HourFormat(prefs.calendarUse12HourFormat);
      setIsIncludeSlots(prefs.calendarIncludeSlots);
      setIsIncludeNotAvailableSlots(prefs.calendarIncludeNotAvailableSlots);
      setIsShowCancelled(prefs.calendarShowCancelled);
      setClinicsQuickFilter(prefs.calendarClinicsQuickFilter);
      setDoctorsQuickFilter(prefs.calendarDoctorsQuickFilter);
      setCalendarClinicOrder(prefs.calendarClinicOrder);
      setCalendarDoctorOrder(prefs.calendarDoctorOrder);
      setCalendarColumnWidthMode(prefs.calendarColumnWidthMode);
      setCalendarColumnWidthCustomPx(prefs.calendarColumnWidthCustomPx);
      setCalendarDayViewGroupBy(prefs.calendarDayViewGroupBy);
      setCalendarDaySplitEnabled(prefs.calendarDaySplitEnabled);
      setCalendarDayStartHour(prefs.calendarDayStartHour);
      setCalendarDayEndHour(prefs.calendarDayEndHour);
      setCalendarTickMinutes(prefs.calendarTickMinutes);
      setCalendarShowSessionRail(prefs.calendarShowSessionRail);
      setCalendarRailWidthPx(prefs.calendarRailWidthPx);
      setCalendarRailColorMode(prefs.calendarRailColorMode);
      setCalendarRailMarginPx(prefs.calendarRailMarginPx);
      setHideSunday(prefs.calendarHideSunday);
      setSavedFiltersExpanded(prefs.calendarFilterBadgesExpanded);
    });
  }, []);

  useEffect(() => subscribeSavedCalendarFilters(setSavedFilters), []);

  // "Show this appointment on the calendar" (today: double-click in the Messages
  // scheduling panel). Declared AFTER the preferences subscription above so the view
  // switch we push here is already being listened for. Two entry points: the buffered
  // target this pane could not hear while hidden (its effects were unmounted), and live
  // requests while the page is open. Landing on the right day is all we do here — the
  // resolve effect below waits for the event itself to show up.
  useEffect(() => {
    const applyTarget = (target) => {
      if (!target) {
        return;
      }
      // Same parse as calendar-data.js: honor the payload's offset, then render in the
      // app's zone so the day key matches the events'.
      const startDateTime = DateTime.fromISO(target.startAt, { setZone: true }).toLocal();
      if (!startDateTime.isValid) {
        return;
      }
      setSelectedDate(startDateTime);
      setUserPreferences({ calendarView: VIEW_TYPES.DAY });
      setPendingFocus({
        appointmentId: target.appointmentId,
        dateKey: toDateKey(startDateTime),
        relaxed: false,
      });
    };
    applyTarget(consumePendingOpenCalendarAppointment());
    return subscribeOpenCalendarAppointment(applyTarget);
  }, []);

  useEffect(() => {
    let isMounted = true;

    const loadTenants = async () => {
      try {
        const response = await fetchTenants();
        if (!isMounted) {
          return;
        }

        // Clinics hidden on the "calendar" surface (Settings → Clinic visibility) never
        // become filter options; a saved preset naming one simply drops it on reconcile.
        const options = filterClinicsForSurface(response?.items, "calendar")
          .map((item) => {
            const id = String(item?.id || item?._id || "").trim();
            if (!id) {
              return null;
            }
            const name = resolveTenantDisplayName(item, id);
            // Compact label for the quick-filter checkbox row: clinics use their
            // bare name (the dropdown keeps the fuller "shortName (name)").
            const quickLabel = String(item?.name || "").trim() || name;
            const country = String(item?.country || "").trim().toUpperCase();
            const timezone = String(item?.timezone || "").trim();
            return { id, name, quickLabel, country, timezone };
          })
          .filter(Boolean);

        setTenantOptions(options);
        const storedTenantIds = getAppointmentFilters().tenantIds;
        if (Array.isArray(storedTenantIds)) {
          const availableTenantIdSet = new Set(options.map((option) => option.id));
          setSelectedTenantIds(storedTenantIds.filter((id) => availableTenantIdSet.has(id)));
        } else {
          setSelectedTenantIds(options.map((option) => option.id));
        }
        hasInitializedTenantSelectionRef.current = true;
      } catch {
        if (!isMounted) {
          return;
        }
        setTenantOptions([]);
        setSelectedTenantIds(Array.isArray(getAppointmentFilters().tenantIds) ? getAppointmentFilters().tenantIds : []);
        hasInitializedTenantSelectionRef.current = true;
      }
    };

    const loadDoctors = async () => {
      try {
        const response = await fetchDoctors();
        if (!isMounted) {
          return;
        }

        const options = (Array.isArray(response?.items) ? response.items : [])
          .map((item) => {
            const id = String(item?.id || item?._id || "").trim();
            if (!id) {
              return null;
            }
            const firstName = String(item?.firstName || "").trim();
            const lastName = String(item?.lastName || "").trim();
            const fullName = `${firstName} ${lastName}`.trim();
            const displayName = String(item?.displayName || "").trim();
            const name = fullName || displayName || id;
            // Quick-filter checkbox row prefers the doctor's displayName; the
            // dropdown keeps the fuller "First Last" name.
            const quickLabel = displayName || name;
            return { id, name, quickLabel };
          })
          .filter(Boolean);

        setDoctorOptions(options);
        const storedDoctorIds = getAppointmentFilters().doctorIds;
        if (Array.isArray(storedDoctorIds)) {
          const availableDoctorIdSet = new Set(options.map((option) => option.id));
          setSelectedDoctorIds(storedDoctorIds.filter((id) => availableDoctorIdSet.has(id)));
        } else {
          setSelectedDoctorIds(options.map((option) => option.id));
        }
        hasInitializedDoctorSelectionRef.current = true;
      } catch {
        if (!isMounted) {
          return;
        }
        setDoctorOptions([]);
        setSelectedDoctorIds(Array.isArray(getAppointmentFilters().doctorIds) ? getAppointmentFilters().doctorIds : []);
        hasInitializedDoctorSelectionRef.current = true;
      }
    };

    const loadDoctorColors = async () => {
      try {
        const map = await getDoctorColorMap();
        if (isMounted) setDoctorColorById(map);
      } catch {
        // fail-soft: chips fall back to the deterministic hash swatch
      }
    };

    // Same reconcile rule as clinics/doctors: a stored null selects every calendar, a
    // stored array is intersected with what actually loaded (stale ids drop out).
    const loadStandaloneCalendars = async () => {
      try {
        const options = await getStandaloneCalendarOptions();
        if (!isMounted) {
          return;
        }
        setStandaloneCalendarOptions(options);
        setSelectedCalendarIds(resolveSavedCalendarIds(getAppointmentFilters().calendarIds, options));
        hasInitializedCalendarSelectionRef.current = true;
      } catch {
        if (!isMounted) {
          return;
        }
        setStandaloneCalendarOptions([]);
        setSelectedCalendarIds(
          Array.isArray(getAppointmentFilters().calendarIds) ? getAppointmentFilters().calendarIds : [],
        );
        hasInitializedCalendarSelectionRef.current = true;
      }
    };

    loadTenants();
    loadDoctors();
    loadDoctorColors();
    loadStandaloneCalendars();
    return () => {
      isMounted = false;
    };
  }, []);

  // Settings → Calendars publishes `calendars-changed` after every mutation: drop the
  // cached calendars index, reload the standalone options (created / renamed /
  // archived calendars) and re-reconcile the selection — a selection that covered
  // every calendar keeps covering them all, so a brand-new calendar shows up at once;
  // a narrowed one just loses ids that no longer exist.
  useEffect(() => {
    let active = true;
    const unsubscribe = subscribeAppointmentRefresh(({ change }) => {
      if (change?.source !== "calendars-changed") {
        return;
      }
      invalidateCalendarsCache();
      getStandaloneCalendarOptions()
        .then((options) => {
          if (!active) {
            return;
          }
          const previous = standaloneCalendarOptionsRef.current;
          setStandaloneCalendarOptions(options);
          setSelectedCalendarIds((current) => {
            const wasAll =
              previous.length === 0 || previous.every((option) => current.includes(option.id));
            if (wasAll) {
              return options.map((option) => option.id);
            }
            const allowed = new Set(options.map((option) => option.id));
            return current.filter((id) => allowed.has(id));
          });
        })
        .catch(() => {
          // fail-soft: keep the options we have
        });
    });
    return () => {
      active = false;
      unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (
      !hasInitializedTenantSelectionRef.current ||
      !hasInitializedDoctorSelectionRef.current ||
      !hasInitializedCalendarSelectionRef.current
    ) {
      return;
    }
    setAppointmentFilters({
      tenantIds: selectedTenantIds,
      doctorIds: selectedDoctorIds,
      // With no standalone calendars loaded the facet is inert: leave the stored value
      // alone so a legacy "all" (null) survives until there are calendars to pick from —
      // writing [] here would hide every calendar created later.
      ...(standaloneCalendarOptions.length > 0 ? { calendarIds: selectedCalendarIds } : {}),
    });
  }, [selectedDoctorIds, selectedTenantIds, selectedCalendarIds, standaloneCalendarOptions.length]);

  useEffect(() => {
    const unsubscribe = subscribeAppointmentFilters((nextFilters) => {
      const nextTenantIds = Array.isArray(nextFilters?.tenantIds) ? nextFilters.tenantIds : [];
      const nextDoctorIds = Array.isArray(nextFilters?.doctorIds) ? nextFilters.doctorIds : [];
      // null = every standalone calendar; resolved against the latest loaded options.
      const nextCalendarIds = resolveSavedCalendarIds(
        nextFilters?.calendarIds,
        standaloneCalendarOptionsRef.current,
      );
      setSelectedTenantIds((currentTenantIds) =>
        areIdArraysEqual(currentTenantIds, nextTenantIds) ? currentTenantIds : nextTenantIds,
      );
      setSelectedDoctorIds((currentDoctorIds) =>
        areIdArraysEqual(currentDoctorIds, nextDoctorIds) ? currentDoctorIds : nextDoctorIds,
      );
      setSelectedCalendarIds((currentCalendarIds) =>
        areIdArraysEqual(currentCalendarIds, nextCalendarIds) ? currentCalendarIds : nextCalendarIds,
      );
    });
    return unsubscribe;
  }, []);

  useEffect(() => {
    return subscribeAppointmentRefresh(({ version }) => {
      setAppointmentRefreshVersion(version);
    });
  }, []);

  const selectedTenantCount = selectedTenantIds.length;
  const allTenantCount = tenantOptions.length;
  const selectedDoctorCount = selectedDoctorIds.length;
  const allDoctorCount = doctorOptions.length;

  // The options in the user's own order. Everything that CARES about order reads
  // these — the quick-filter chips, the dropdown facets, the saved-filter bar, and
  // (via selectedTenants/selectedDoctors below) the day-view columns. The raw
  // state arrays stay untouched for the .length / .find lookups elsewhere.
  const orderedTenantOptions = useMemo(
    () => orderByIds(tenantOptions, calendarClinicOrder),
    [tenantOptions, calendarClinicOrder],
  );
  const orderedDoctorOptions = useMemo(
    () => orderByIds(doctorOptions, calendarDoctorOrder),
    [doctorOptions, calendarDoctorOrder],
  );

  // Each drop writes back the WHOLE resulting id list, so the stored order is
  // always complete and self-contained rather than a patch over a stale one.
  const handleReorderTenants = useCallback(
    (activeId, overId) => {
      const toIndex = orderedTenantOptions.findIndex((t) => t.id === overId);
      if (toIndex < 0) return;
      setUserPreferences({
        calendarClinicOrder: moveIdToIndex(orderedTenantOptions, activeId, toIndex),
      });
    },
    [orderedTenantOptions],
  );
  const handleReorderDoctors = useCallback(
    (activeId, overId) => {
      const toIndex = orderedDoctorOptions.findIndex((d) => d.id === overId);
      if (toIndex < 0) return;
      setUserPreferences({
        calendarDoctorOrder: moveIdToIndex(orderedDoctorOptions, activeId, toIndex),
      });
    },
    [orderedDoctorOptions],
  );
  const handleSortTenantsByName = useCallback(() => {
    setUserPreferences({ calendarClinicOrder: idsSortedByName(orderedTenantOptions) });
  }, [orderedTenantOptions]);
  const handleSortDoctorsByName = useCallback(() => {
    setUserPreferences({ calendarDoctorOrder: idsSortedByName(orderedDoctorOptions) });
  }, [orderedDoctorOptions]);

  const selectedTenants = useMemo(() => {
    if (!selectedTenantIds.length || !orderedTenantOptions.length) {
      return [];
    }
    const idSet = new Set(selectedTenantIds);
    return orderedTenantOptions.filter((t) => idSet.has(t.id));
  }, [orderedTenantOptions, selectedTenantIds]);

  const selectedDoctors = useMemo(() => {
    if (!selectedDoctorIds.length || !orderedDoctorOptions.length) {
      return [];
    }
    const idSet = new Set(selectedDoctorIds);
    return orderedDoctorOptions.filter((d) => idSet.has(d.id));
  }, [orderedDoctorOptions, selectedDoctorIds]);

  // Standalone calendars: the id set the column/filter helpers use to recognise a
  // standalone event, a name lookup for labels, and the selected options (facet order).
  const standaloneIdSet = useMemo(
    () => new Set(standaloneCalendarOptions.map((option) => option.id)),
    [standaloneCalendarOptions],
  );
  const calendarNameById = useMemo(() => {
    const map = new Map();
    for (const option of standaloneCalendarOptions) {
      if (option?.id && option?.name) map.set(option.id, option.name);
    }
    return map;
  }, [standaloneCalendarOptions]);
  const selectedCalendars = useMemo(() => {
    if (!selectedCalendarIds.length || !standaloneCalendarOptions.length) {
      return [];
    }
    const idSet = new Set(selectedCalendarIds);
    return standaloneCalendarOptions.filter((option) => idSet.has(option.id));
  }, [standaloneCalendarOptions, selectedCalendarIds]);

  // Columns (with their name headers) render whenever the grouped facet has an
  // explicit selection — including a single clinic/doctor, so the column title
  // still says which one is on screen — followed by one column per selected
  // standalone calendar (in BOTH group-by modes). Null (nothing selected at all)
  // falls back to one untitled "_all" column; when the entity facet has no loaded
  // options but calendar columns exist, an explicit "_all" pool column keeps the
  // doctor/clinic events visible next to them.
  const dayViewColumns = useMemo(
    () =>
      buildDayViewColumns({
        groupBy: calendarDayViewGroupBy,
        selectedDoctors,
        selectedTenants,
        selectedCalendars,
        entityFacetLoaded:
          calendarDayViewGroupBy === "doctor" ? doctorOptions.length > 0 : tenantOptions.length > 0,
      }),
    [
      calendarDayViewGroupBy,
      selectedDoctors,
      selectedTenants,
      selectedCalendars,
      doctorOptions.length,
      tenantOptions.length,
    ],
  );

  // Standalone-calendar events pass iff their calendar is selected (they have no
  // clinic/doctor to be filtered by); everything else keeps the tenant + doctor
  // membership rule; cancelled events drop last unless shown.
  const filteredEvents = useMemo(
    () =>
      filterCalendarEvents(events, {
        tenantFilterEnabled: tenantOptions.length > 0,
        tenantIdSet: new Set(selectedTenantIds),
        doctorFilterEnabled: doctorOptions.length > 0,
        doctorIdSet: new Set(selectedDoctorIds),
        calendarIdSet: new Set(selectedCalendarIds),
        standaloneIdSet,
        showCancelled: isShowCancelled,
        isCancelled: isCancelledCalendarEvent,
      }),
    [
      doctorOptions.length,
      events,
      isShowCancelled,
      selectedDoctorIds,
      selectedTenantIds,
      selectedCalendarIds,
      standaloneIdSet,
      tenantOptions.length,
    ],
  );

  const toggleTenantSelection = (tenantId) => {
    setSelectedTenantIds((currentSelectedTenantIds) =>
      currentSelectedTenantIds.includes(tenantId)
        ? currentSelectedTenantIds.filter((id) => id !== tenantId)
        : [...currentSelectedTenantIds, tenantId],
    );
  };
  const handleSelectAllTenants = () => {
    setSelectedTenantIds(tenantOptions.map((tenant) => tenant.id));
  };
  const handleDeselectAllTenants = () => {
    setSelectedTenantIds([]);
  };
  const toggleDoctorSelection = (doctorId) => {
    setSelectedDoctorIds((currentSelectedDoctorIds) =>
      currentSelectedDoctorIds.includes(doctorId)
        ? currentSelectedDoctorIds.filter((id) => id !== doctorId)
        : [...currentSelectedDoctorIds, doctorId],
    );
  };
  const handleSelectAllDoctors = () => {
    setSelectedDoctorIds(doctorOptions.map((doctor) => doctor.id));
  };
  const handleDeselectAllDoctors = () => {
    setSelectedDoctorIds([]);
  };
  const toggleCalendarSelection = (calendarId) => {
    setSelectedCalendarIds((currentSelectedCalendarIds) =>
      currentSelectedCalendarIds.includes(calendarId)
        ? currentSelectedCalendarIds.filter((id) => id !== calendarId)
        : [...currentSelectedCalendarIds, calendarId],
    );
  };
  const handleSelectAllCalendars = () => {
    setSelectedCalendarIds(standaloneCalendarOptions.map((option) => option.id));
  };
  const handleDeselectAllCalendars = () => {
    setSelectedCalendarIds([]);
  };
  // Calendar-item color theme for a doctor chip: built from their calendar (Plato)
  // color when known, else a deterministic per-id hue so the row is fully colored.
  // Renders the whole name like an event card (see QuickFacetRow's themed variant).
  const doctorColorTheme = useCallback(
    (opt) => {
      const hex =
        doctorColorById.get(String(opt?.id || "").trim()) || doctorSwatchFallback(opt?.id);
      return buildEventColorTheme(hex);
    },
    [doctorColorById],
  );

  const eventsByDate = useMemo(() => groupEventsByDate(filteredEvents), [filteredEvents]);
  const selectedDayEvents = useMemo(() => eventsByDate[toDateKey(selectedDate)] || [], [eventsByDate, selectedDate]);
  const daySplitEvents = useMemo(
    () => (effectiveDaySplitDate ? eventsByDate[toDateKey(effectiveDaySplitDate)] || [] : []),
    [eventsByDate, effectiveDaySplitDate],
  );

  // Second half of the deep link: wait for the requested appointment to actually be on
  // screen, then focus + highlight it. Re-runs as the day's fetch lands. If the event
  // exists but the active filters hide it, open those filters up (once) rather than
  // dropping the user on a day where the appointment they asked for is invisible.
  useEffect(() => {
    if (!pendingFocus || toDateKey(selectedDate) !== pendingFocus.dateKey) {
      return;
    }
    if (selectedDayEvents.some((event) => event.id === pendingFocus.appointmentId)) {
      setDayViewFocusedEventId(pendingFocus.appointmentId);
      setAppointmentHighlight({
        eventId: pendingFocus.appointmentId,
        dateKey: pendingFocus.dateKey,
      });
      setFocusSignal((signal) => signal + 1);
      setPendingFocus(null);
      return;
    }
    if (pendingFocus.relaxed) {
      return;
    }
    const hiddenEvent = events.find((event) => event.id === pendingFocus.appointmentId);
    if (!hiddenEvent) {
      return; // still fetching this day
    }

    const tenantId = String(hiddenEvent.tenantId || "").trim();
    const doctorId = String(hiddenEvent.doctorId || "").trim();
    const calendarId = String(hiddenEvent.calendarId || "").trim();
    const revealed = [];
    if (isStandaloneCalendarEvent(hiddenEvent, standaloneIdSet)) {
      // A standalone-calendar entry is only visible through its calendar's column.
      if (calendarId && standaloneIdSet.has(calendarId) && !selectedCalendarIds.includes(calendarId)) {
        setSelectedCalendarIds((current) =>
          current.includes(calendarId) ? current : [...current, calendarId],
        );
        revealed.push(`calendar ${calendarNameById.get(calendarId) || calendarId}`);
      }
    } else {
      if (tenantOptions.length > 0 && tenantId && !selectedTenantIds.includes(tenantId)) {
        setSelectedTenantIds((current) =>
          current.includes(tenantId) ? current : [...current, tenantId],
        );
        revealed.push(`clinic ${tenantOptions.find((o) => o.id === tenantId)?.name || tenantId}`);
      }
      if (doctorOptions.length > 0 && doctorId && !selectedDoctorIds.includes(doctorId)) {
        setSelectedDoctorIds((current) =>
          current.includes(doctorId) ? current : [...current, doctorId],
        );
        revealed.push(`doctor ${doctorOptions.find((o) => o.id === doctorId)?.name || doctorId}`);
      }
    }
    if (!isShowCancelled && isCancelledCalendarEvent(hiddenEvent)) {
      setUserPreferences({ calendarShowCancelled: true });
      revealed.push("cancelled visits");
    }
    if (revealed.length > 0) {
      toast.info(`Calendar filters expanded to show this appointment (added ${revealed.join(", ")}).`);
    }
    setPendingFocus((current) => (current ? { ...current, relaxed: true } : current));
  }, [
    pendingFocus,
    selectedDate,
    selectedDayEvents,
    events,
    tenantOptions,
    doctorOptions,
    selectedTenantIds,
    selectedDoctorIds,
    selectedCalendarIds,
    standaloneIdSet,
    calendarNameById,
    isShowCancelled,
  ]);

  // Don't wait forever: a deleted appointment (or one the backend no longer returns for
  // this day) would otherwise leave the request pending and silently do nothing.
  useEffect(() => {
    if (!pendingFocus) {
      return undefined;
    }
    const timer = setTimeout(() => {
      setPendingFocus(null);
      toast.error("Couldn't find that appointment on the calendar.");
    }, 12000);
    return () => clearTimeout(timer);
  }, [pendingFocus]);

  // The highlight is derived, not cleared by an effect: leaving the day or switching view
  // hides it, coming back to the same day in day view shows it again, and clicking another
  // appointment drops it for good (see onDismissHighlight below).
  const highlightedEventId =
    appointmentHighlight &&
    activeView === VIEW_TYPES.DAY &&
    toDateKey(selectedDate) === appointmentHighlight.dateKey
      ? appointmentHighlight.eventId
      : null;
  const weekDays = weekDayWindow.days;
  const monthCells = useMemo(
    () => getMonthMatrix(selectedDate, { hideSunday }),
    [selectedDate, hideSunday],
  );

  // Fetch time-off / holiday blocks for the active view's range. Errors are
  // swallowed inside the hook (non-admins silently get an empty list).
  const { from: blocksRangeStart, to: blocksRangeEnd } = useMemo(() => {
    if (activeView === VIEW_TYPES.WEEK) {
      // The rendered run spills into the neighbouring weeks — cover all of it so
      // padding days get their holiday overlays and all-day chips too.
      return {
        from: startOfDay(weekDayWindow.days[0]),
        to: endOfDay(weekDayWindow.days[weekDayWindow.days.length - 1]),
      };
    }
    // Stretch the month range to also cover the second day pane when it sits
    // in a different month.
    let from = startOfMonth(selectedDate);
    let to = endOfMonth(selectedDate);
    if (effectiveDaySplitDate) {
      const splitFrom = startOfMonth(effectiveDaySplitDate);
      const splitTo = endOfMonth(effectiveDaySplitDate);
      if (splitFrom < from) from = splitFrom;
      if (splitTo > to) to = splitTo;
    }
    return { from, to };
  }, [activeView, selectedDate, weekDayWindow, effectiveDaySplitDate]);
  const { blocks: timeOffBlocks } = useTimeOffBlocks({
    fromDate: blocksRangeStart,
    toDate: blocksRangeEnd,
    refreshKey: appointmentRefreshVersion,
  });

  const title = useMemo(() => {
    if (activeView === VIEW_TYPES.DAY) {
      return formatDayHeading(selectedDate);
    }
    if (activeView === VIEW_TYPES.WEEK) {
      return formatWeekRange(selectedDate);
    }
    return formatMonthYear(selectedDate);
  }, [activeView, selectedDate]);
  const isMonthView = activeView === VIEW_TYPES.MONTH;
  const isDayView = activeView === VIEW_TYPES.DAY;

  // Shared time-grid geometry for the day & week views — the visible hour window,
  // tick spacing, and zoom collapsed into row templates and placement helpers.
  const timeAxis = useMemo(
    () =>
      getCalendarTimeAxis({
        startHour: calendarDayStartHour,
        endHour: calendarDayEndHour,
        tickMinutes: calendarTickMinutes,
        zoomPercent: calendarZoomPercent,
      }),
    [calendarDayStartHour, calendarDayEndHour, calendarTickMinutes, calendarZoomPercent],
  );

  // Live view mirror so the zoom/pan hook's handlers stay stable (no listener churn on every
  // wheel notch) while still reading the current view.
  const activeViewRef = useRef(activeView);
  activeViewRef.current = activeView;

  // Each view registers its own scroll viewport (a view contains nested ScrollAreas, so the
  // wrapper can't reliably resolve it); the hook reads/writes scroll on the active one.
  const registerViewport = useCallback((el) => {
    activeViewportRef.current = el;
  }, []);
  const getViewport = useCallback(() => activeViewportRef.current, []);
  // Read straight from the store: setUserPreferences updates it synchronously, so rapid wheel
  // notches in the same frame each see the previous notch's value and accumulate correctly.
  // Vertical = hour-row/cell-height zoom; horizontal = column-width zoom (null in month).
  const getActiveZoom = useCallback(() => {
    const prefs = getUserPreferences();
    if (activeViewRef.current === VIEW_TYPES.MONTH) {
      return { vertical: prefs.calendarMonthZoomPercent, horizontal: null };
    }
    return { vertical: prefs.calendarZoomPercent, horizontal: prefs.calendarWidthZoomPercent };
  }, []);
  const applyZoom = useCallback((updates) => {
    if (activeViewRef.current === VIEW_TYPES.MONTH) {
      if (updates.vertical != null) {
        setUserPreferences({ calendarMonthZoomPercent: updates.vertical });
      }
      return;
    }
    const patch = {};
    if (updates.vertical != null) patch.calendarZoomPercent = updates.vertical;
    if (updates.horizontal != null) patch.calendarWidthZoomPercent = updates.horizontal;
    if (Object.keys(patch).length > 0) setUserPreferences(patch);
  }, []);

  useCalendarZoomPan({
    wrapperRef,
    getViewport,
    activeView,
    zoomAnchorRef,
    getZoom: getActiveZoom,
    onZoom: applyZoom,
    verticalMin: isMonthView ? MONTH_ZOOM_MIN_PERCENT : ZOOM_MIN_PERCENT,
    verticalMax: isMonthView ? MONTH_ZOOM_MAX_PERCENT : ZOOM_MAX_PERCENT,
    horizontalMin: isMonthView ? null : WIDTH_ZOOM_MIN_PERCENT,
    horizontalMax: isMonthView ? null : WIDTH_ZOOM_MAX_PERCENT,
  });

  // "Reset view" (Alt/⌥+0 or the toolbar button): zoom back to 100% and re-anchor the
  // scroll (to now for day/week, to top for month). Scope is zoom + scroll only — the
  // selected date, view, and filters are left untouched. No cursor anchor: reset re-centers.
  const handleResetView = useCallback(() => {
    zoomAnchorRef.current = null;
    if (activeViewRef.current === VIEW_TYPES.MONTH) {
      setUserPreferences({ calendarMonthZoomPercent: 100 });
    } else {
      setUserPreferences({ calendarZoomPercent: 100, calendarWidthZoomPercent: 100 });
    }
    setResetSignal((n) => n + 1);
  }, []);

  // "Fit view" (Alt/⌥+Shift+0): set the vertical zoom to the smallest value at which every
  // rendered event card shows its full content — zooming out when over-zoomed, in when
  // clipped — clamped to the max when full visibility isn't reachable. Day/week only;
  // horizontal zoom and scroll position are left alone (a viewport-center anchor keeps
  // the view in place, reusing the wheel-zoom compensation path in each view).
  const handleFitZoom = useCallback(() => {
    if (activeViewRef.current === VIEW_TYPES.MONTH) {
      return;
    }
    const fit = computeFitZoomPercent({
      container: sectionRef.current,
      minPercent: ZOOM_MIN_PERCENT,
      maxPercent: ZOOM_MAX_PERCENT,
    });
    // No cards to measure, or already at the fit value: do nothing. The no-change case
    // must not write an anchor — the views only consume it on an actual zoom change,
    // and a stale one would mis-anchor the next zoom.
    if (fit == null || fit === getUserPreferences().calendarZoomPercent) {
      return;
    }
    const viewport = activeViewportRef.current;
    zoomAnchorRef.current = viewport
      ? {
          view: activeViewRef.current,
          pointerOffsetX: viewport.clientWidth / 2,
          pointerOffsetY: viewport.clientHeight / 2,
          contentX: viewport.scrollLeft + viewport.clientWidth / 2,
          contentY: viewport.scrollTop + viewport.clientHeight / 2,
          prevScrollWidth: viewport.scrollWidth,
          prevScrollHeight: viewport.scrollHeight,
          axes: { vertical: true, horizontal: false },
        }
      : null;
    setUserPreferences({ calendarZoomPercent: fit });
  }, []);

  // Press Alt/⌥+0 to reset, or Alt/⌥+Shift+0 to fit-zoom, but only while the calendar
  // holds focus and the user isn't typing. Cmd/Ctrl+0 is intentionally NOT used — the
  // Electron shell binds it to page-zoom reset. Matched by event.code because both
  // Shift and macOS Option mutate event.key ("0" → ")" / "º" on a US layout).
  useEffect(() => {
    const onKeyDown = (event) => {
      const isFitCombo =
        event.code === "Digit0" &&
        event.altKey &&
        event.shiftKey &&
        !event.metaKey &&
        !event.ctrlKey;
      const isResetCombo =
        event.code === "Digit0" &&
        event.altKey &&
        !event.shiftKey &&
        !event.metaKey &&
        !event.ctrlKey;
      if (!isFitCombo && !isResetCombo) {
        return;
      }
      if (!calendarKeyEventAllowed(event, sectionRef.current)) {
        return;
      }
      if (isFitCombo) {
        if (activeViewRef.current === VIEW_TYPES.MONTH) {
          return;
        }
        event.preventDefault();
        handleFitZoom();
        return;
      }
      event.preventDefault();
      handleResetView();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [handleFitZoom, handleResetView]);

  // Apply a saved preset: restore its tenant/doctor selection. Saved ids are reconciled
  // against the loaded options so a stale id can't linger — but only when that facet
  // actually has options. If a facet failed to load (empty), its saved ids are kept verbatim
  // rather than silently wiped (the facet filter is disabled while it has no options anyway).
  // Declared before the 1–9 effect below so its binding is initialized when that effect's
  // dependency array reads it.
  const applySavedFilter = useCallback(
    (filter) => {
      if (!filter) {
        return;
      }
      const reconcile = (ids, options) => {
        const savedIds = Array.isArray(ids) ? ids : [];
        if (options.length === 0) {
          return savedIds;
        }
        const available = new Set(options.map((option) => option.id));
        return savedIds.filter((id) => available.has(id));
      };
      setSelectedTenantIds(reconcile(filter.tenantIds, tenantOptions));
      setSelectedDoctorIds(reconcile(filter.doctorIds, doctorOptions));
      // Calendars: a preset saved before the facet existed has calendarIds null and
      // keeps meaning "all standalone calendars"; a saved array is intersected.
      setSelectedCalendarIds(resolveSavedCalendarIds(filter.calendarIds, standaloneCalendarOptions));
      // Restore the day-view "Group" toggle too. Older presets predate this field; the
      // store normalizes it, so filter.groupBy is always a valid mode. Writing the
      // preference syncs `calendarDayViewGroupBy` back through subscribeUserPreferences.
      if (filter.groupBy) {
        setUserPreferences({ calendarDayViewGroupBy: filter.groupBy });
      }
    },
    [tenantOptions, doctorOptions, standaloneCalendarOptions],
  );

  // Press Alt/⌥+1–9 to apply the saved preset in that slot — same focus/typing
  // guards as the Alt+0 reset above. Matched by event.code because macOS Option
  // mutates event.key (⌥1 → "¡"); Ctrl+Alt is rejected so AltGr layouts still type.
  // The ref mirror lets the handler read the latest list without re-binding on
  // every change.
  useEffect(() => {
    const onKeyDown = (event) => {
      if (!event.altKey || event.metaKey || event.ctrlKey || event.shiftKey) {
        return;
      }
      const match = /^Digit([1-9])$/.exec(event.code);
      if (!match) {
        return;
      }
      if (!calendarKeyEventAllowed(event, sectionRef.current)) {
        return;
      }
      const preset = savedFiltersRef.current[Number(match[1]) - 1];
      if (!preset) {
        return;
      }
      event.preventDefault();
      applySavedFilter(preset);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [applySavedFilter]);

  // Mirror the calendar's keyboard shortcuts into the Electron View ▸ Calendar
  // menu: report page/view/preset state (the menu grays items out when the
  // calendar isn't active) and perform the action when a menu item is clicked.
  // No-op outside Electron (vite preview has no window.electronAPI).
  useEffect(() => {
    const api = window.electronAPI;
    if (!api?.setCalendarMenuState || !api?.onCalendarShortcut) {
      return undefined;
    }
    api.setCalendarMenuState({
      active: true,
      view: activeView,
      savedFilters: savedFilters.map((filter) => filter.name),
    });
    return api.onCalendarShortcut(({ action, index } = {}) => {
      if (action === "reset-view") {
        handleResetView();
      } else if (action === "fit-zoom") {
        if (activeViewRef.current !== VIEW_TYPES.MONTH) {
          handleFitZoom();
        }
      } else if (action === "saved-filter") {
        const preset = savedFiltersRef.current[Number(index)];
        if (preset) {
          applySavedFilter(preset);
        }
      }
    });
  }, [activeView, savedFilters, handleResetView, handleFitZoom, applySavedFilter]);

  // Gray the menu items back out when the Calendar page unmounts. Separate
  // unmount-only effect so re-renders of the state-reporting effect above
  // don't flicker the menu through an inactive state.
  useEffect(() => {
    return () => {
      window.electronAPI?.setCalendarMenuState?.({ active: false });
    };
  }, []);

  // The "Reset view" affordance shows whenever the active view is zoomed on either axis.
  const isViewZoomed = isMonthView
    ? calendarMonthZoomPercent !== 100
    : calendarZoomPercent !== 100 || calendarWidthZoomPercent !== 100;

  // Hour-row-height presets vs. a free custom value. Picking a preset closes the
  // custom input; picking "Custom" opens it (keeping the current value).
  const handleZoomChange = (id) => {
    if (id === "custom") {
      setZoomCustomOpen(true);
    } else {
      setZoomCustomOpen(false);
      setUserPreferences({ calendarZoomPercent: id });
    }
  };

  // Grid-width presets vs. a free custom value (same pattern as the zoom control).
  const handleWidthZoomChange = (id) => {
    if (id === "custom") {
      setWidthZoomCustomOpen(true);
    } else {
      setWidthZoomCustomOpen(false);
      setUserPreferences({ calendarWidthZoomPercent: id });
    }
  };

  // Slot-opacity presets vs. a free custom value (same pattern as the zoom control).
  const handleSlotOpacityChange = (id) => {
    if (id === "custom") {
      setSlotOpacityCustomOpen(true);
    } else {
      setSlotOpacityCustomOpen(false);
      setUserPreferences({ calendarSlotOpacityPercent: id });
    }
  };

  // "2 days" split: the second pane defaults back to selectedDate+1 on the next
  // enable, so an explicitly chosen date doesn't linger across toggles.
  const handleToggleDaySplit = () => {
    if (calendarDaySplitEnabled) {
      setDaySplitDate(null);
      setUserPreferences({ calendarDaySplitEnabled: false });
    } else {
      setUserPreferences({ calendarDaySplitEnabled: true });
    }
  };
  const handleCloseDaySplit = useCallback(() => {
    setDaySplitDate(null);
    setUserPreferences({ calendarDaySplitEnabled: false });
  }, []);

  // The three-way "Slots" toggle is backed by two booleans.
  const slotsMode = !isIncludeSlots ? "hide" : isIncludeNotAvailableSlots ? "all" : "free";
  const handleSlotsModeChange = (mode) => {
    if (mode === "hide") {
      setUserPreferences({ calendarIncludeSlots: false });
    } else if (mode === "all") {
      setUserPreferences({ calendarIncludeSlots: true, calendarIncludeNotAvailableSlots: true });
    } else {
      setUserPreferences({ calendarIncludeSlots: true, calendarIncludeNotAvailableSlots: false });
    }
  };

  // A filter is "active" when its options have loaded and the selection is a
  // strict subset — drives the Reset affordance in the header.
  const tenantsNarrowed = allTenantCount > 0 && selectedTenantCount !== allTenantCount;
  const doctorsNarrowed = allDoctorCount > 0 && selectedDoctorCount !== allDoctorCount;
  const allCalendarCount = standaloneCalendarOptions.length;
  const calendarsNarrowed = allCalendarCount > 0 && selectedCalendars.length !== allCalendarCount;
  const filtersNarrowed = tenantsNarrowed || doctorsNarrowed || calendarsNarrowed;
  const handleResetFilters = () => {
    handleSelectAllTenants();
    handleSelectAllDoctors();
    handleSelectAllCalendars();
  };

  const handleSaveCurrentFilter = (name) => {
    addSavedCalendarFilter({
      name,
      tenantIds: selectedTenantIds,
      doctorIds: selectedDoctorIds,
      calendarIds: selectedCalendarIds,
      groupBy: calendarDayViewGroupBy,
    });
  };

  const handleToggleSavedFiltersExpanded = () => {
    setUserPreferences({ calendarFilterBadgesExpanded: !savedFiltersExpanded });
  };

  // Saving is only meaningful once at least one facet has loaded options to capture.
  const canSaveFilter =
    tenantOptions.length > 0 || doctorOptions.length > 0 || standaloneCalendarOptions.length > 0;

  // Double-click on empty time (or the title-row "+") in a standalone-calendar column:
  // snap the clicked minute to the quarter hour, default to a 30-minute entry kept
  // inside the visible window, and open the "New entry" sheet for that calendar.
  const handleOpenEntryDraft = useCallback(
    ({ calendarId, dayKey, minute }) => {
      const option = standaloneCalendarOptions.find((candidate) => candidate.id === calendarId);
      if (!option || !dayKey) return;
      const range = directEntryRangeFromMinute(minute, {
        snapMinutes: 15,
        durationMinutes: 30,
        startMinutes: timeAxis.startMinutes,
        endMinutes: timeAxis.endMinutes,
      });
      setEntryDraft({
        calendarId,
        calendarName: option.name,
        colorHex: option.colorHex || "",
        localOnly: Boolean(option.localOnly),
        date: dayKey,
        startTime: minutesToHhmm(range.startMinutes),
        endTime: minutesToHhmm(range.endMinutes),
      });
    },
    [standaloneCalendarOptions, timeAxis.startMinutes, timeAxis.endMinutes],
  );

  const navigate = (step) => {
    setSelectedDate((currentDate) => {
      if (activeView === VIEW_TYPES.DAY) {
        return addDays(currentDate, step);
      }
      if (activeView === VIEW_TYPES.WEEK) {
        return addDays(currentDate, step * 7);
      }
      return addMonths(currentDate, step);
    });
  };

  const goToToday = () => {
    setSelectedDate(DateTime.now());
    setIsDatePickerOpen(false);
  };

  // Scroll the day view to a session start time. We anchor on the hour marker in
  // the time gutter and let scrollIntoView resolve the (variable, sticky) header
  // offset — the anchor carries a scroll-margin so it lands just below the sticky
  // "All day" bar. This avoids duplicating the grid's pixels-per-minute geometry.
  const scrollDayViewToHour = useCallback(
    (hour) => {
      const viewport = activeViewportRef.current;
      if (!viewport) return;
      const anchor = viewport.querySelector(`[data-hour="${hour}"]`);
      if (anchor) {
        anchor.scrollIntoView({ block: "start", inline: "nearest", behavior: "smooth" });
        return;
      }
      // The requested hour falls outside the configured visible window (no anchor
      // rendered): settle on the nearest edge rather than doing nothing.
      const maxScrollTop = Math.max(viewport.scrollHeight - viewport.clientHeight, 0);
      viewport.scrollTo({
        top: hour * 60 <= timeAxis.startMinutes ? 0 : maxScrollTop,
        behavior: "smooth",
      });
    },
    [timeAxis.startMinutes],
  );

  // The picker hands back a JS Date at local midnight; preserve the existing
  // time-of-day/zone and only move the calendar day.
  const handlePickDate = (jsDate) => {
    if (!jsDate) return;
    setSelectedDate((currentDate) =>
      currentDate.set({
        year: jsDate.getFullYear(),
        month: jsDate.getMonth() + 1,
        day: jsDate.getDate(),
      })
    );
    setIsDatePickerOpen(false);
  };

  // Open the picker showing the selected date's month.
  const handleDatePickerOpenChange = (open) => {
    if (open) {
      setPickerMonth(selectedDate.toJSDate());
    }
    setIsDatePickerOpen(open);
  };

  const stepPickerMonth = useCallback((direction) => {
    setPickerMonth((prev) => {
      const next = new Date(prev ?? new Date());
      next.setDate(1); // avoid month-length overflow (e.g. Jan 31 → Mar)
      next.setMonth(next.getMonth() + direction);
      return next;
    });
  }, []);

  // Wheel over the open picker pages through months. React registers `wheel`
  // passively (preventDefault is a no-op there), and the picker lives in a
  // portal that mounts/unmounts with the popover — so attach a native
  // non-passive listener via a callback ref that fires exactly on mount,
  // sidestepping any effect-vs-portal timing race.
  const datePickerWheelRef = useCallback(
    (node) => {
      if (datePickerWheelCleanupRef.current) {
        datePickerWheelCleanupRef.current();
        datePickerWheelCleanupRef.current = null;
      }
      if (!node) return;
      const onWheel = (event) => {
        const delta = event.deltaY || event.deltaX;
        if (!delta) return;
        event.preventDefault();
        stepPickerMonth(delta > 0 ? 1 : -1);
      };
      node.addEventListener("wheel", onWheel, { passive: false });
      datePickerWheelCleanupRef.current = () =>
        node.removeEventListener("wheel", onWheel);
    },
    [stepPickerMonth]
  );

  const doctorNameById = useMemo(() => {
    const map = new Map();
    for (const option of doctorOptions) {
      if (option?.id && option?.name) map.set(option.id, option.name);
    }
    return map;
  }, [doctorOptions]);
  const tenantNameById = useMemo(() => {
    const map = new Map();
    for (const option of tenantOptions) {
      if (option?.id && option?.name) map.set(option.id, option.name);
    }
    return map;
  }, [tenantOptions]);

  // Strictly pointer-based: an open-slot hit under the pointer wins (exact-time
  // snap), then any column surface. No rect-intersection fallback — the ghost
  // is card-sized, so falling back to its rect would keep "hitting" a column
  // (and commit a drop) long after the pointer left the grid.
  const collisionDetection = useCallback((args) => {
    const pointerCollisions = pointerWithin(args);
    if (pointerCollisions.length === 0) return [];
    const typeOf = (collision) =>
      args.droppableContainers.find((container) => container.id === collision.id)?.data?.current
        ?.type;
    const slotHit = pointerCollisions.find((collision) => typeOf(collision) === "slot");
    if (slotHit) return [slotHit];
    const columnHit = pointerCollisions.find((collision) => typeOf(collision) === "column" || typeOf(collision) === "month-day");
    if (columnHit) return [columnHit];
    return pointerCollisions;
  }, []);

  // Resolve where a drag would land: an open slot adopts the slot's exact time
  // (and owner), a column surface maps the pointer's Y to the 5-minute lattice.
  // Geometry is measured fresh from the rendered column so zoom, row
  // stretching and mid-drag auto-scroll are all absorbed.
  const computeDropTarget = useCallback(
    (active, over, delta, activatorEvent) => {
      const dragEvent = active?.data?.current?.event;
      const state = dragStateRef.current;
      const overData = over?.data?.current;
      if (!dragEvent || !state || !overData) return null;
      const duration = state.durationMinutes;

      if (overData.type === "slot") {
        const slot = overData.event;
        if (!slot?.startAt) return null;
        // Standalone-calendar entries only move in time — never onto an open slot
        // (that would hand them a doctor/clinic).
        if (!canDropOnSlot(dragEvent, standaloneIdSet)) return null;
        return {
          kind: "slot",
          slot,
          dayKey: toDateKey(slot.startAt),
          // Column identity only matters for the day view's split columns —
          // week-view columns are days, so the landing placeholder matches on
          // dayKey alone there.
          columnId:
            activeView === VIEW_TYPES.DAY && dayViewColumns
              ? (calendarDayViewGroupBy === "doctor" ? slot.doctorId : slot.tenantId) || null
              : null,
          startAt: slot.startAt,
          endAt: slot.startAt.plus({ minutes: duration }),
        };
      }

      if (overData.type === "column") {
        // Standalone entries land only on their own calendar column (or a week day
        // cell); doctor/clinic appointments never land on a calendar column.
        if (!canDropOnColumn(dragEvent, overData, standaloneIdSet)) return null;
        const selector = `[data-time-col="${String(over.id).replace(/"/g, '\\"')}"]`;
        const geometry = measureColumnGeometry(document.querySelector(selector), timeAxis);
        // Live pointer position tracked by our own listener. dnd-kit's `delta`
        // is scroll-adjusted (pointer translate + accumulated container
        // scroll), and our fresh rect measurement ALSO reflects that scroll —
        // adding delta to the start position would count auto-scroll twice.
        const clientY =
          state.lastClientY ?? ((activatorEvent?.clientY ?? NaN) + (delta?.y ?? 0));
        if (!geometry || !Number.isFinite(clientY)) return null;
        const rawStart = geometry.minuteAtClientY(clientY) - state.grabOffsetMinutes;

        // Hysteresis: a drop back in the event's own column/day within one
        // snap step of where it started lands EXACTLY on the original time —
        // a sloppy 6-10px click that armed the drag must never commit a
        // 5-minute move (activation is distance-based, so this is common).
        const originalStartMinutes = dragEvent.startAt.hour * 60 + dragEvent.startAt.minute;
        const sameDay = overData.dayKey === toDateKey(dragEvent.startAt);
        const sameColumn =
          !overData.columnId ||
          (overData.kind === "calendar"
            ? overData.calendarId === dragEvent.calendarId
            : overData.groupBy === "doctor"
              ? overData.columnId === dragEvent.doctorId
              : overData.columnId === dragEvent.tenantId);
        if (sameDay && sameColumn && Math.abs(rawStart - originalStartMinutes) < SNAP_MINUTES) {
          return {
            kind: "column",
            dayKey: overData.dayKey,
            columnId: overData.columnId || null,
            columnKind: overData.kind || null,
            calendarId: overData.calendarId || null,
            groupBy: overData.groupBy || null,
            startAt: dragEvent.startAt,
            endAt: dragEvent.endAt,
          };
        }

        const startMinutes = clampStartMinutes(snapMinutes(rawStart), duration, timeAxis);
        const startAt = DateTime.fromISO(overData.dayKey).set({
          hour: Math.floor(startMinutes / 60),
          minute: startMinutes % 60,
          second: 0,
          millisecond: 0,
        });
        if (!startAt.isValid) return null;
        return {
          kind: "column",
          dayKey: overData.dayKey,
          columnId: overData.columnId || null,
          columnKind: overData.kind || null,
          calendarId: overData.calendarId || null,
          groupBy: overData.groupBy || null,
          startAt,
          endAt: startAt.plus({ minutes: duration }),
        };
      }

      if (overData.type === "month-day") {
        // Month view: move to another date, keep the wall-clock time + length.
        const day = DateTime.fromISO(overData.dayKey);
        if (!day.isValid) return null;
        const startAt = day.set({
          hour: dragEvent.startAt.hour,
          minute: dragEvent.startAt.minute,
          second: 0,
          millisecond: 0,
        });
        return {
          kind: "month-day",
          startAt,
          endAt: startAt.plus({ minutes: duration }),
        };
      }

      return null;
    },
    [timeAxis, activeView, dayViewColumns, calendarDayViewGroupBy, standaloneIdSet],
  );

  const handleDragStart = ({ active, activatorEvent }) => {
    const dragEvent = active?.data?.current?.event;
    setActiveDragEvent(dragEvent || null);
    setDragPreview(null);
    if (!dragEvent || dragEvent.kind !== "appointment") {
      dragStateRef.current = null;
      return;
    }
    const durationMinutes = Math.max(
      Math.round(dragEvent.endAt.diff(dragEvent.startAt, "minutes").minutes),
      5,
    );
    const clientY = Number.isFinite(activatorEvent?.clientY) ? activatorEvent.clientY : null;
    // Where inside the card the user grabbed it, in minutes — keeps the card
    // pinned under the pointer instead of jumping its top edge to the cursor.
    let grabOffsetMinutes = 0;
    const columnEl = closestTimeGeometryElement(activatorEvent?.target);
    const geometry = columnEl ? measureColumnGeometry(columnEl, timeAxis) : null;
    if (geometry && clientY != null) {
      const eventStartMinutes = dragEvent.startAt.hour * 60 + dragEvent.startAt.minute;
      grabOffsetMinutes = Math.min(
        Math.max(geometry.minuteAtClientY(clientY) - eventStartMinutes, 0),
        durationMinutes,
      );
    }
    const initialRect = active?.rect?.current?.initial;
    // Track the raw pointer ourselves: dnd-kit's delta is scroll-adjusted, so
    // it can't be combined with freshly measured rects (see computeDropTarget).
    const onPointerMove = (e) => {
      if (dragStateRef.current) dragStateRef.current.lastClientY = e.clientY;
    };
    window.addEventListener("pointermove", onPointerMove);
    dragStateRef.current = {
      durationMinutes,
      grabOffsetMinutes,
      startClientY: clientY,
      lastClientY: null,
      width: initialRect?.width ?? null,
      height: initialRect?.height ?? null,
      // Palette of the dragged card so the landing placeholder can tint to match.
      theme: buildEventColorTheme(dragEvent.colorHex),
      newPatient: isNewPatient(dragEvent),
      cleanup: () => window.removeEventListener("pointermove", onPointerMove),
    };
  };

  const handleDragMove = ({ active, over, delta, activatorEvent }) => {
    if (!dragStateRef.current) return;
    const target = computeDropTarget(active, over, delta, activatorEvent);
    if (!target) {
      setDragPreview((previous) => (previous == null ? previous : null));
      return;
    }
    const startLabel = formatTime(target.startAt, { hour12: isDay12HourFormat });
    const endLabel = formatTime(target.endAt, { hour12: isDay12HourFormat });
    let contextLabel = "";
    if (target.kind === "slot") {
      contextLabel = target.slot.doctor || "";
    } else if (target.columnKind === "calendar") {
      contextLabel = calendarNameById.get(target.calendarId || "") || "";
    } else if (target.columnId) {
      contextLabel =
        target.groupBy === "doctor"
          ? doctorNameById.get(target.columnId) || ""
          : tenantNameById.get(target.columnId) || "";
    }
    const label = `${startLabel} – ${endLabel} · ${target.startAt.toFormat("ccc d LLL")}${
      contextLabel ? ` · ${contextLabel}` : ""
    }`;
    // Dashed in-grid placeholder marking the snapped landing position (day and
    // week views; month cells highlight via their droppable ring instead).
    let placeholder = null;
    if (target.kind === "column" || target.kind === "slot") {
      const position = eventGridPositionForAxis(
        { startAt: target.startAt, endAt: target.endAt },
        timeAxis,
      );
      placeholder = {
        dayKey: target.dayKey,
        columnId: target.columnId ?? null,
        startRow: position.startRow,
        span: position.span,
        // Constant per drag, so it stays out of the change-detection signature.
        theme: dragStateRef.current.theme,
        newPatient: dragStateRef.current.newPatient,
      };
    }
    // Only re-render when the snapped result actually changes (once per 5-min step).
    const signature = `${label}|${placeholder?.dayKey}|${placeholder?.columnId}|${placeholder?.startRow}|${placeholder?.span}`;
    setDragPreview((previous) =>
      previous?.signature === signature ? previous : { label, placeholder, signature },
    );
  };

  const handleDragEnd = ({ active, over, delta, activatorEvent }) => {
    const dragEvent = active?.data?.current?.event;
    const target = computeDropTarget(active, over, delta, activatorEvent);
    setActiveDragEvent(null);
    setDragPreview(null);
    dragStateRef.current?.cleanup?.();
    dragStateRef.current = null;
    if (!dragEvent || !target || !canMutateAppointmentEvent(dragEvent)) return;

    const changes = { startAt: target.startAt, endAt: target.endAt };
    if (target.kind === "slot") {
      const slot = target.slot;
      if (slot.doctorId && slot.doctorId !== dragEvent.doctorId) {
        changes.doctorId = slot.doctorId;
        if (slot.doctor) changes.doctor = slot.doctor;
      }
      if (slot.tenantId && slot.tenantId !== dragEvent.tenantId) changes.tenantId = slot.tenantId;
      if (slot.calendarId && slot.calendarId !== dragEvent.calendarId) {
        changes.calendarId = slot.calendarId;
      }
    } else if (target.kind === "column" && target.columnId && target.columnKind !== "calendar") {
      // A calendar column's id ("cal:<id>") is never an owner — a drop there (only ever
      // the entry's own calendar, see canDropOnColumn) is a pure time move.
      if (target.groupBy === "doctor" && target.columnId !== dragEvent.doctorId) {
        changes.doctorId = target.columnId;
        const doctorName = doctorNameById.get(target.columnId);
        if (doctorName) changes.doctor = doctorName;
      } else if (target.groupBy === "tenant" && target.columnId !== dragEvent.tenantId) {
        changes.tenantId = target.columnId;
      }
    }

    const timeUnchanged =
      changes.startAt.toMillis() === dragEvent.startAt.toMillis() &&
      changes.endAt.toMillis() === dragEvent.endAt.toMillis();
    const ownerUnchanged =
      changes.doctorId === undefined &&
      changes.tenantId === undefined &&
      changes.calendarId === undefined;
    if (timeUnchanged && ownerUnchanged) return;

    const movedTo = `${target.startAt.toFormat("ccc d LLL")}, ${formatTime(target.startAt, {
      hour12: isDay12HourFormat,
    })}`;
    const contextSuffix = changes.doctor
      ? ` · ${changes.doctor}`
      : changes.tenantId
        ? ` · ${tenantNameById.get(changes.tenantId) || ""}`
        : "";
    applyAppointmentUpdate({
      event: dragEvent,
      changes,
      toastLabel: `Appointment moved to ${movedTo}${contextSuffix}`,
    });
  };

  const handleDragCancel = () => {
    setActiveDragEvent(null);
    setDragPreview(null);
    dragStateRef.current?.cleanup?.();
    dragStateRef.current = null;
  };

  return (
    <section ref={sectionRef} className="flex h-full min-h-0 flex-col overflow-hidden">
      <header className="@container/toolbar sticky top-0 z-40 flex flex-none items-center gap-x-3 gap-y-2 border-b border-border bg-background/95 px-6 py-3 backdrop-blur">
        {/* Date navigation + date picker */}
        <div className="inline-flex h-9 shrink-0 items-center overflow-hidden rounded-lg border border-border bg-card shadow-xs">
          <button
            type="button"
            className="flex h-full w-9 items-center justify-center text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            onClick={() => navigate(-1)}
          >
            <span className="sr-only">Previous</span>
            <ChevronLeft className="h-4 w-4" />
          </button>
          <Popover open={isDatePickerOpen} onOpenChange={handleDatePickerOpenChange}>
            <PopoverTrigger asChild>
              <button
                type="button"
                title="Pick a date"
                aria-label="Pick a date"
                className="flex h-full items-center gap-1.5 border-x border-border px-3.5 text-sm font-semibold text-foreground transition-colors hover:bg-muted"
              >
                {title}
                <ChevronDown className="h-3.5 w-3.5 text-muted-foreground/70" />
              </button>
            </PopoverTrigger>
            <PopoverContent align="center" className="w-auto p-0">
              <div ref={datePickerWheelRef}>
                <DatePickerCalendar
                  mode="single"
                  selected={selectedDate.toJSDate()}
                  month={pickerMonth ?? selectedDate.toJSDate()}
                  onMonthChange={setPickerMonth}
                  onSelect={handlePickDate}
                  autoFocus
                />
              </div>
              <div className="border-t border-border p-2">
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full justify-center font-medium"
                  onClick={goToToday}
                >
                  Today
                </Button>
              </div>
            </PopoverContent>
          </Popover>
          <button
            type="button"
            className="flex h-full w-9 items-center justify-center text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            onClick={() => navigate(1)}
          >
            <span className="sr-only">Next</span>
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>

        {/* One-click jump back to today (day view) — dimmed once already there. */}
        {isDayView ? (
          <button
            type="button"
            onClick={goToToday}
            disabled={isToday(selectedDate)}
            title="Jump to today"
            className={cn(
              "inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-border bg-card shadow-xs transition-colors",
              isToday(selectedDate)
                ? "text-muted-foreground/50"
                : "text-primary hover:bg-primary/10",
            )}
          >
            <span className="sr-only">Jump to today</span>
            <CalendarCheck2 className="h-4 w-4" />
          </button>
        ) : null}

        {/* Session jumps (day view only): scroll straight to the AM (08:00) or
            PM (13:00) block without leaving the current day. */}
        {isDayView ? (
          <div className="inline-flex h-9 shrink-0 items-center overflow-hidden rounded-lg border border-border bg-card shadow-xs">
            <button
              type="button"
              onClick={() => scrollDayViewToHour(AM_SESSION_HOUR)}
              title="Jump to morning session (8:00 AM)"
              className="flex h-full items-center gap-1.5 px-3 text-xs font-medium text-foreground transition-colors hover:bg-muted"
            >
              {/* <Sunrise className="h-4 w-4 text-amber-500" /> */}
              AM
            </button>
            <button
              type="button"
              onClick={() => scrollDayViewToHour(PM_SESSION_HOUR)}
              title="Jump to afternoon session (1:00 PM)"
              className="flex h-full items-center gap-1.5 border-l border-border px-3 text-xs font-medium text-foreground transition-colors hover:bg-muted"
            >
              {/* <Sunset className="h-4 w-4 text-orange-500" /> */}
              PM
            </button>
          </div>
        ) : null}

        <div className="ml-auto flex min-w-0 items-center justify-end gap-2 [&>*]:shrink-0 @max-[64rem]/toolbar:flex-wrap">
          {/* View switcher */}
          <SegmentedToggle
            size="md"
            ariaLabel="Calendar view"
            value={activeView}
            segments={VIEW_CONFIG}
            onChange={(id) => setUserPreferences({ calendarView: id })}
          />

          {/* Group by (day view only) */}
          {isDayView ? (
            <div className="flex items-center gap-1.5">
              <span className="text-[11px] font-medium text-muted-foreground @max-[100rem]/toolbar:hidden">
                Group
              </span>
              <SegmentedToggle
                size="md"
                ariaLabel="Day view group by"
                value={calendarDayViewGroupBy}
                segments={DAY_VIEW_GROUP_BY_MODES}
                onChange={(id) => setUserPreferences({ calendarDayViewGroupBy: id })}
              />
            </div>
          ) : null}

          {/* Two-day split (day view only) */}
          {isDayView ? (
            <button
              type="button"
              onClick={handleToggleDaySplit}
              aria-pressed={calendarDaySplitEnabled}
              title="Show two days side by side"
              className={cn(
                "inline-flex h-9 items-center gap-1.5 whitespace-nowrap rounded-lg border px-3 text-sm font-medium shadow-xs transition-colors",
                calendarDaySplitEnabled
                  ? "border-primary/50 bg-primary/10 text-primary hover:bg-primary/15"
                  : "border-border bg-card text-foreground hover:bg-muted",
              )}
            >
              <Columns2 className={cn("h-4 w-4", !calendarDaySplitEnabled && "text-muted-foreground")} />
              <span className="@max-[100rem]/toolbar:hidden">Split</span>
            </button>
          ) : null}

          {/* The divider separates the left cluster from the facet dropdowns — shown
              only while at least one facet is still presented inline as a dropdown.
              Facets switched to quick-checkbox mode render in their own bar below. */}
          {(tenantOptions.length > 0 && !clinicsQuickFilter) ||
          (doctorOptions.length > 0 && !doctorsQuickFilter) ||
          standaloneCalendarOptions.length > 0 ? (
            <span className="block h-6 w-px bg-border @max-[100rem]/toolbar:hidden" />
          ) : null}

          {/* Entity filters — searchable faceted multi-selects. When a facet is set to
              quick-checkbox mode it drops out here and appears in the quick-filter bar. */}
          {tenantOptions.length > 0 && !clinicsQuickFilter ? (
            <FacetFilter
              size="md"
              icon={Building2}
              label="Clinics"
              options={orderedTenantOptions}
              selectedIds={selectedTenantIds}
              onToggle={toggleTenantSelection}
              onSelectAll={handleSelectAllTenants}
              onClear={handleDeselectAllTenants}
              searchPlaceholder="Search clinics…"
              align="end"
            />
          ) : null}
          {doctorOptions.length > 0 && !doctorsQuickFilter ? (
            <FacetFilter
              size="md"
              icon={Stethoscope}
              label="Doctors"
              options={orderedDoctorOptions}
              selectedIds={selectedDoctorIds}
              onToggle={toggleDoctorSelection}
              onSelectAll={handleSelectAllDoctors}
              onClear={handleDeselectAllDoctors}
              searchPlaceholder="Search doctors…"
              align="end"
            />
          ) : null}
          {/* Standalone admin calendars — dropdown only (no quick-checkbox mode); hidden
              until at least one active standalone calendar exists. */}
          {standaloneCalendarOptions.length > 0 ? (
            <FacetFilter
              size="md"
              icon={CalendarDays}
              label="Calendars"
              options={standaloneCalendarOptions}
              selectedIds={selectedCalendarIds}
              onToggle={toggleCalendarSelection}
              onSelectAll={handleSelectAllCalendars}
              onClear={handleDeselectAllCalendars}
              dotColor={(option) => option.colorHex}
              searchPlaceholder="Search calendars…"
              align="end"
            />
          ) : null}
          {filtersNarrowed ? (
            <button
              type="button"
              onClick={handleResetFilters}
              title={
                standaloneCalendarOptions.length > 0
                  ? "Show all clinics, doctors and calendars"
                  : "Show all clinics and doctors"
              }
              className="inline-flex h-9 items-center gap-1.5 whitespace-nowrap rounded-lg px-2.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            >
              <FilterX className="h-4 w-4" />
              <span className="@max-[100rem]/toolbar:hidden">Reset</span>
            </button>
          ) : null}

          {/* Reset zoom & scroll (also bound to Alt/⌥+0) — shown only when zoomed. */}
          {isViewZoomed ? (
            <button
              type="button"
              onClick={handleResetView}
              title={`Reset zoom and scroll (${ALT_KEY_LABEL}0)`}
              className="inline-flex h-9 items-center gap-1.5 whitespace-nowrap rounded-lg px-2.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            >
              <RotateCcw className="h-4 w-4" />
              <span className="@max-[100rem]/toolbar:hidden">Reset view</span>
            </button>
          ) : null}

          <span className="block h-6 w-px bg-border @max-[100rem]/toolbar:hidden" />

          {/* Display settings */}
          <Popover>
            <PopoverTrigger asChild>
              <button
                type="button"
                title="Display options"
                className="inline-flex h-9 items-center gap-1.5 whitespace-nowrap rounded-lg border border-border bg-card px-3 text-sm font-medium text-foreground shadow-xs transition-colors hover:bg-muted"
              >
                <SlidersHorizontal className="h-4 w-4 text-muted-foreground" />
                <span className="@max-[100rem]/toolbar:hidden">Display</span>
                <ChevronDown className="h-3.5 w-3.5 text-muted-foreground/70" />
              </button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-96 p-0">
              <div className="border-b border-border px-3 py-2.5">
                <p className="text-xs font-semibold text-foreground">Display options</p>
              </div>
              <ScrollArea viewportClassName="max-h-[70vh] overscroll-contain">
                <div className="divide-y divide-border">
                <SettingSection title="General">
                  <SettingRow icon={Clock} label="Time of day">
                    <SegmentedToggle
                      size="sm"
                      ariaLabel="Time format"
                      value={isDay12HourFormat ? "12h" : "24h"}
                      segments={TIME_FORMAT_SEGMENTS}
                      onChange={(id) => setUserPreferences({ calendarUse12HourFormat: id === "12h" })}
                    />
                  </SettingRow>
                  <SettingRow icon={Ban} label="Cancelled">
                    <SegmentedToggle
                      size="sm"
                      ariaLabel="Cancelled appointments"
                      value={isShowCancelled ? "show" : "hide"}
                      segments={CANCELLED_SEGMENTS}
                      onChange={(id) => setUserPreferences({ calendarShowCancelled: id === "show" })}
                    />
                  </SettingRow>
                </SettingSection>

                {/* Month shows bookings only (MonthView drops slots), so its
                    slot controls would be dead switches — hide them there. */}
                {!isMonthView ? (
                  <SettingSection title="Slots">
                    <SettingRow icon={CalendarCheck2} label="Slots">
                      <SegmentedToggle
                        size="sm"
                        ariaLabel="Slots display"
                        value={slotsMode}
                        segments={SLOTS_SEGMENTS}
                        onChange={handleSlotsModeChange}
                      />
                    </SettingRow>
                    <SettingRow icon={Droplet} label="Slot opacity" layout="stack">
                      <SegmentedToggle
                        size="sm"
                        stretch
                        ariaLabel="Slot opacity"
                        value={slotOpacityCustomOpen ? "custom" : calendarSlotOpacityPercent}
                        segments={SLOT_OPACITY_SEGMENTS}
                        onChange={handleSlotOpacityChange}
                      />
                      {slotOpacityCustomOpen ? (
                        <div className="flex justify-end">
                          <StepperInput
                            value={calendarSlotOpacityPercent}
                            onChange={(pct) => setUserPreferences({ calendarSlotOpacityPercent: pct })}
                            min={SLOT_OPACITY_MIN_PERCENT}
                            max={SLOT_OPACITY_MAX_PERCENT}
                            step={5}
                            suffix="%"
                            ariaLabel="Custom slot opacity percent"
                          />
                        </div>
                      ) : null}
                    </SettingRow>
                  </SettingSection>
                ) : null}

                {tenantOptions.length > 0 || doctorOptions.length > 0 ? (
                  <SettingSection title="Filters">
                    {tenantOptions.length > 0 ? (
                      <SettingRow icon={Building2} label="Clinics">
                        <SegmentedToggle
                          size="sm"
                          ariaLabel="Clinics filter style"
                          value={clinicsQuickFilter ? "checkboxes" : "dropdown"}
                          segments={FILTER_STYLE_SEGMENTS}
                          onChange={(id) =>
                            setUserPreferences({ calendarClinicsQuickFilter: id === "checkboxes" })
                          }
                        />
                      </SettingRow>
                    ) : null}
                    {doctorOptions.length > 0 ? (
                      <SettingRow icon={Stethoscope} label="Doctors">
                        <SegmentedToggle
                          size="sm"
                          ariaLabel="Doctors filter style"
                          value={doctorsQuickFilter ? "checkboxes" : "dropdown"}
                          segments={FILTER_STYLE_SEGMENTS}
                          onChange={(id) =>
                            setUserPreferences({ calendarDoctorsQuickFilter: id === "checkboxes" })
                          }
                        />
                      </SettingRow>
                    ) : null}
                  </SettingSection>
                ) : null}

                <SettingSection
                  title={isMonthView ? "Month view" : isDayView ? "Day view" : "Week view"}
                >
                  {isDayView ? (
                    <SettingRow icon={Columns2} label="Column width" layout="stack">
                      <SegmentedToggle
                        size="sm"
                        stretch
                        ariaLabel="Day column width"
                        value={calendarColumnWidthMode}
                        segments={COLUMN_WIDTH_SEGMENTS}
                        onChange={(id) => setUserPreferences({ calendarColumnWidthMode: id })}
                      />
                      {calendarColumnWidthMode === "custom" ? (
                        <div className="flex justify-end">
                          <StepperInput
                            value={calendarColumnWidthCustomPx}
                            onChange={(px) => setUserPreferences({ calendarColumnWidthCustomPx: px })}
                            min={COLUMN_WIDTH_MIN_PX}
                            max={COLUMN_WIDTH_MAX_PX}
                            step={20}
                            suffix="px"
                            ariaLabel="Custom column width in pixels"
                          />
                        </div>
                      ) : null}
                    </SettingRow>
                  ) : null}
                  {isDayView ? (
                    <SettingRow icon={PanelLeft} label="Session rail" layout="stack">
                      <SegmentedToggle
                        size="sm"
                        stretch
                        ariaLabel="Session rail"
                        value={calendarShowSessionRail ? "on" : "off"}
                        segments={SESSION_RAIL_SEGMENTS}
                        onChange={(id) => setUserPreferences({ calendarShowSessionRail: id === "on" })}
                      />
                      {calendarShowSessionRail ? (
                        <>
                          <SegmentedToggle
                            size="sm"
                            stretch
                            ariaLabel="Session rail color"
                            value={calendarRailColorMode}
                            segments={RAIL_COLOR_MODE_SEGMENTS}
                            onChange={(id) => setUserPreferences({ calendarRailColorMode: id })}
                          />
                          <div className="flex items-center justify-between gap-3">
                            <span className="text-xs text-muted-foreground">Width</span>
                            <StepperInput
                              value={calendarRailWidthPx}
                              onChange={(px) => setUserPreferences({ calendarRailWidthPx: px })}
                              min={RAIL_WIDTH_MIN_PX}
                              max={RAIL_WIDTH_MAX_PX}
                              step={2}
                              suffix="px"
                              ariaLabel="Session rail width in pixels"
                            />
                          </div>
                          <div className="flex items-center justify-between gap-3">
                            <span className="text-xs text-muted-foreground">Left margin</span>
                            <StepperInput
                              value={calendarRailMarginPx}
                              onChange={(px) => setUserPreferences({ calendarRailMarginPx: px })}
                              min={RAIL_MARGIN_MIN_PX}
                              max={RAIL_MARGIN_MAX_PX}
                              step={1}
                              suffix="px"
                              ariaLabel="Session rail left margin in pixels"
                            />
                          </div>
                        </>
                      ) : null}
                    </SettingRow>
                  ) : null}
                  {!isMonthView ? (
                    <SettingRow icon={Clock} label="Visible hours" layout="stack">
                      <div className="flex items-center gap-2">
                        <StepperInput
                          value={calendarDayStartHour}
                          onChange={(hour) => setUserPreferences({ calendarDayStartHour: hour })}
                          min={DAY_START_HOUR_MIN}
                          max={calendarDayEndHour - 1}
                          step={1}
                          suffix=":00"
                          ariaLabel="Day start hour"
                        />
                        <span className="text-xs text-muted-foreground">to</span>
                        <StepperInput
                          value={calendarDayEndHour}
                          onChange={(hour) => setUserPreferences({ calendarDayEndHour: hour })}
                          min={calendarDayStartHour + 1}
                          max={DAY_END_HOUR_MAX}
                          step={1}
                          suffix=":00"
                          ariaLabel="Day end hour"
                        />
                      </div>
                    </SettingRow>
                  ) : null}
                  {!isMonthView ? (
                    <SettingRow icon={Ruler} label="Min interval" layout="stack">
                      <SegmentedToggle
                        size="sm"
                        stretch
                        ariaLabel="Minimum tick interval"
                        value={calendarTickMinutes}
                        segments={TICK_MINUTES_SEGMENTS}
                        onChange={(id) => setUserPreferences({ calendarTickMinutes: id })}
                      />
                    </SettingRow>
                  ) : null}
                  {!isMonthView ? (
                    <SettingRow icon={Rows3} label="Hour row height" layout="stack">
                      <SegmentedToggle
                        size="sm"
                        stretch
                        ariaLabel="Hour row height"
                        value={zoomCustomOpen ? "custom" : calendarZoomPercent}
                        segments={ZOOM_SEGMENTS}
                        onChange={handleZoomChange}
                      />
                      {zoomCustomOpen ? (
                        <div className="flex justify-end">
                          <StepperInput
                            value={calendarZoomPercent}
                            onChange={(pct) => setUserPreferences({ calendarZoomPercent: pct })}
                            min={ZOOM_MIN_PERCENT}
                            max={ZOOM_MAX_PERCENT}
                            step={10}
                            suffix="%"
                            ariaLabel="Custom hour row height percent"
                          />
                        </div>
                      ) : null}
                    </SettingRow>
                  ) : null}
                  {!isMonthView ? (
                    <SettingRow icon={Columns2} label="Grid width" layout="stack">
                      <SegmentedToggle
                        size="sm"
                        stretch
                        ariaLabel="Grid width"
                        value={widthZoomCustomOpen ? "custom" : calendarWidthZoomPercent}
                        segments={WIDTH_ZOOM_SEGMENTS}
                        onChange={handleWidthZoomChange}
                      />
                      {widthZoomCustomOpen ? (
                        <div className="flex justify-end">
                          <StepperInput
                            value={calendarWidthZoomPercent}
                            onChange={(pct) => setUserPreferences({ calendarWidthZoomPercent: pct })}
                            min={WIDTH_ZOOM_MIN_PERCENT}
                            max={WIDTH_ZOOM_MAX_PERCENT}
                            step={10}
                            suffix="%"
                            ariaLabel="Custom grid width percent"
                          />
                        </div>
                      ) : null}
                    </SettingRow>
                  ) : null}
                  {isMonthView ? (
                    <SettingRow icon={LayoutGrid} label="Cell height">
                      <StepperInput
                        value={calendarMonthZoomPercent}
                        onChange={(pct) => setUserPreferences({ calendarMonthZoomPercent: pct })}
                        min={MONTH_ZOOM_MIN_PERCENT}
                        max={MONTH_ZOOM_MAX_PERCENT}
                        step={10}
                        suffix="%"
                        ariaLabel="Month cell height percent"
                      />
                    </SettingRow>
                  ) : null}
                </SettingSection>
                </div>
              </ScrollArea>
            </PopoverContent>
          </Popover>
        </div>
      </header>

      {/* Quick-filter bar — facets switched to checkbox mode in Display render here as
          an inline row of toggles, sharing the same selection state as the dropdowns. */}
      {(clinicsQuickFilter && tenantOptions.length > 0) ||
      (doctorsQuickFilter && doctorOptions.length > 0) ? (
        <div className="flex flex-none flex-col gap-2 border-b border-border bg-background/95 px-6 py-2.5">
          {clinicsQuickFilter && tenantOptions.length > 0 ? (
            <QuickFacetRow
              icon={Building2}
              label="Clinics"
              options={orderedTenantOptions}
              selectedIds={selectedTenantIds}
              onToggle={toggleTenantSelection}
              onSelectAll={handleSelectAllTenants}
              onClear={handleDeselectAllTenants}
              onReorder={handleReorderTenants}
              onSortByName={handleSortTenantsByName}
            />
          ) : null}
          {doctorsQuickFilter && doctorOptions.length > 0 ? (
            <QuickFacetRow
              icon={Stethoscope}
              label="Doctors"
              options={orderedDoctorOptions}
              selectedIds={selectedDoctorIds}
              onToggle={toggleDoctorSelection}
              onSelectAll={handleSelectAllDoctors}
              onClear={handleDeselectAllDoctors}
              colorTheme={doctorColorTheme}
              onReorder={handleReorderDoctors}
              onSortByName={handleSortDoctorsByName}
            />
          ) : null}
        </div>
      ) : null}

      <SavedFiltersBar
        filters={savedFilters}
        currentTenantIds={selectedTenantIds}
        currentDoctorIds={selectedDoctorIds}
        currentCalendarIds={selectedCalendarIds}
        currentGroupBy={calendarDayViewGroupBy}
        tenantOptions={orderedTenantOptions}
        doctorOptions={orderedDoctorOptions}
        calendarOptions={standaloneCalendarOptions}
        expanded={savedFiltersExpanded}
        canSave={canSaveFilter}
        onToggleExpanded={handleToggleSavedFiltersExpanded}
        onApply={applySavedFilter}
        onSave={handleSaveCurrentFilter}
        onDelete={removeSavedCalendarFilter}
        onRename={renameSavedCalendarFilter}
        onReorder={reorderSavedCalendarFilters}
      />

      <DndContext
        sensors={canManageCalendar ? sensors : []}
        collisionDetection={collisionDetection}
        onDragStart={handleDragStart}
        onDragMove={handleDragMove}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
        <CalendarMutationsProvider value={calendarMutationsValue}>
        <div
          ref={wrapperRef}
          tabIndex={0}
          className="relative min-h-0 flex-1 overflow-hidden outline-none"
          style={{ "--calendar-slot-opacity": calendarSlotOpacityPercent / 100 }}
        >
          {isLoading ? (
            <div
              className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 rounded-md border border-border/50 bg-card/50 text-muted-foreground backdrop-blur-[1px]"
              aria-live="polite"
              aria-busy="true"
            >
              <Spinner className="size-8" />
              <span className="text-sm font-medium">Loading events...</span>
            </div>
          ) : null}

          {activeView === VIEW_TYPES.DAY ? (
            <DayView
              selectedDate={selectedDate}
              events={selectedDayEvents}
              splitDate={effectiveDaySplitDate}
              splitDayEvents={daySplitEvents}
              onSelectSplitDate={setDaySplitDate}
              onCloseSplit={handleCloseDaySplit}
              columnWidthMode={calendarColumnWidthMode}
              columnWidthCustomPx={calendarColumnWidthCustomPx}
              showSessionRail={calendarShowSessionRail}
              railWidthPx={calendarRailWidthPx}
              railColorMode={calendarRailColorMode}
              railMarginPx={calendarRailMarginPx}
              dayViewColumns={dayViewColumns}
              dayViewGroupBy={calendarDayViewGroupBy}
              standaloneIdSet={standaloneIdSet}
              onCreateCalendarEntry={canManageCalendar ? handleOpenEntryDraft : undefined}
              dragPlaceholder={dragPreview?.placeholder || null}
              eventsByDate={eventsByDate}
              onSelectDate={setSelectedDate}
              onNavigateMonth={(step) => setSelectedDate((current) => addMonths(current, step))}
              use12HourFormat={isDay12HourFormat}
              zoomPercent={calendarZoomPercent}
              widthZoomPercent={calendarWidthZoomPercent}
              timeAxis={timeAxis}
              registerViewport={registerViewport}
              zoomAnchorRef={zoomAnchorRef}
              resetSignal={resetSignal}
              initialScrollToNow={!hasInitiallyScrolledToNowRef.current && !dayViewFocusedEventId}
              initialScrollRatio={dayViewFocusedEventId ? null : savedScrollRatioRef.current}
              initialSelectedEventId={dayViewFocusedEventId}
              focusEventId={highlightedEventId}
              focusSignal={focusSignal}
              highlightedEventId={highlightedEventId}
              onDismissHighlight={() => setAppointmentHighlight(null)}
              onSaveScrollRatio={(ratio) => {
                savedScrollRatioRef.current = ratio;
              }}
              onDidInitialScroll={() => {
                hasInitiallyScrolledToNowRef.current = true;
                setDayViewFocusedEventId(null);
              }}
              tenantOptions={tenantOptions}
              doctorOptions={doctorOptions}
              onSlotsChanged={() => setAppointmentRefreshVersion((v) => v + 1)}
              timeOffBlocks={timeOffBlocks}
              onSessionAction={
                canManageScheduling && calendarDayViewGroupBy === "doctor"
                  ? (doctorId, paneDate) =>
                      setSessionOverrideTarget({
                        doctorId,
                        date: toDateKey(paneDate),
                      })
                  : undefined
              }
            />
          ) : null}
          {activeView === VIEW_TYPES.WEEK ? (
            <WeekView
              weekDays={weekDays}
              dayWindow={weekDayWindow}
              widthZoomPercent={calendarWidthZoomPercent}
              dragPlaceholder={dragPreview?.placeholder || null}
              eventsByDate={eventsByDate}
              selectedDate={selectedDate}
              onSelectDate={setSelectedDate}
              use12HourFormat={isDay12HourFormat}
              zoomPercent={calendarZoomPercent}
              timeAxis={timeAxis}
              registerViewport={registerViewport}
              zoomAnchorRef={zoomAnchorRef}
              resetSignal={resetSignal}
              initialScrollToNow={!hasInitiallyScrolledToNowRef.current}
              initialScrollRatio={savedScrollRatioRef.current}
              onSaveScrollRatio={(ratio) => {
                savedScrollRatioRef.current = ratio;
              }}
              onDidInitialScroll={() => {
                hasInitiallyScrolledToNowRef.current = true;
              }}
              timeOffBlocks={timeOffBlocks}
              doctorOptions={doctorOptions}
            />
          ) : null}
          {activeView === VIEW_TYPES.MONTH ? (
            <MonthView
              monthCells={monthCells}
              eventsByDate={eventsByDate}
              selectedDate={selectedDate}
              onSelectDate={setSelectedDate}
              onSelectEvent={(date, event) => {
                setSelectedDate(date);
                setUserPreferences({ calendarView: VIEW_TYPES.DAY });
                setDayViewFocusedEventId(event.id);
              }}
              timeOffBlocks={timeOffBlocks}
              doctorOptions={doctorOptions}
              hideSunday={hideSunday}
              monthZoomPercent={calendarMonthZoomPercent}
              registerViewport={registerViewport}
              zoomAnchorRef={zoomAnchorRef}
              resetSignal={resetSignal}
            />
          ) : null}
        </div>
        </CalendarMutationsProvider>
        {/* No drop animation: the optimistic update already re-renders the card
            at its landing position, so animating the ghost back would visibly
            slide toward a stale rect — instant swap reads as faster. */}
        <DragOverlay zIndex={50} dropAnimation={null}>
          {activeDragEvent
            ? (() => {
                // Ghost wears the dragged card's palette (CSS-var theme when the
                // calendar has a color, emerald/violet fallback otherwise) so the
                // preview reads as the same appointment, not a generic chip.
                const ghostTheme = buildEventColorTheme(activeDragEvent.colorHex);
                const ghostNewPatient = isNewPatient(activeDragEvent);
                return (
                  <div
                    style={{
                      width: dragStateRef.current?.width ?? undefined,
                      height: dragStateRef.current?.height ?? undefined,
                      ...(ghostTheme || {}),
                    }}
                    className={cn(
                      "pointer-events-none flex min-w-32 flex-col justify-between overflow-hidden rounded-xl border p-2 pl-3 shadow-xl ring-2 backdrop-blur-sm before:absolute before:inset-y-0 before:left-0 before:w-[3px] before:rounded-l-[inherit] before:content-[''] relative",
                      // Border tint rides on the local `--border` var: the app-wide
                      // unlayered `* { @apply border-border }` (app.css) outranks every
                      // border-color utility, but it reads from --border — so overriding
                      // the var is the one channel that actually lands.
                      ghostTheme
                        ? "[--border:var(--event-border-light)] dark:[--border:var(--event-border-dark)] bg-(--event-bg-light) ring-(--event-border-light) before:bg-(--event-text-light) dark:bg-(--event-bg-dark) dark:ring-(--event-border-dark) dark:before:bg-(--event-text-dark)"
                        : ghostNewPatient
                          ? "[--border:color-mix(in_oklab,var(--color-violet-300)_80%,transparent)] dark:[--border:color-mix(in_oklab,var(--color-violet-500)_40%,transparent)] bg-violet-50/95 ring-violet-300/60 before:bg-violet-500 dark:bg-violet-950/90 dark:ring-violet-500/40 dark:before:bg-violet-400"
                          : "[--border:var(--color-emerald-200)] dark:[--border:color-mix(in_oklab,var(--color-emerald-700)_50%,transparent)] bg-emerald-50/95 ring-emerald-300/60 before:bg-emerald-500 dark:bg-[#0f2419]/95 dark:ring-emerald-600/40 dark:before:bg-emerald-400",
                    )}
                  >
                    <p
                      className={cn(
                        "truncate text-[0.8125rem] font-bold sm:text-sm",
                        ghostTheme
                          ? "text-(--event-text-light) dark:text-(--event-text-dark)"
                          : ghostNewPatient
                            ? "text-violet-950 dark:text-violet-50"
                            : "text-foreground dark:text-white",
                      )}
                    >
                      {activeDragEvent.patient || activeDragEvent.title || "Appointment"}
                    </p>
                    {dragPreview?.label ? (
                      <p
                        className={cn(
                          "mt-1 truncate text-[0.6875rem] font-semibold tabular-nums",
                          ghostTheme
                            ? "text-(--event-text-light) opacity-85 dark:text-(--event-text-dark)"
                            : ghostNewPatient
                              ? "text-violet-700 dark:text-violet-300"
                              : "text-emerald-700 dark:text-emerald-300",
                        )}
                      >
                        {dragPreview.label}
                      </p>
                    ) : null}
                  </div>
                );
              })()
            : null}
        </DragOverlay>
      </DndContext>
      {/* Reason-for-change prompt shared by every amendment path (drag, resize,
          editor, context menu) — see useCalendarAppointmentMutations. */}
      {reasonDialogElement}
      {/* Confirmation "review then send" dialog shared by the context menu,
          editor save and slot-booking flows. */}
      {confirmationDialogElement}
      {/* Per-day session override, opened from a doctor column's title action.
          Self-fetches that doctor+date's session occurrences. */}
      <SessionOverrideDialog
        open={Boolean(sessionOverrideTarget)}
        onOpenChange={(next) => {
          if (!next) setSessionOverrideTarget(null);
        }}
        doctorId={sessionOverrideTarget?.doctorId || ""}
        date={sessionOverrideTarget?.date || ""}
        tenantOptions={tenantOptions}
        doctorOptions={doctorOptions}
        onApplied={() => setAppointmentRefreshVersion((v) => v + 1)}
      />
      {/* "New entry" for a standalone-calendar column (double-click empty time or the
          title-row "+"). Posts a direct entry; the realtime bridge refetches the grid. */}
      <CalendarEntryFormSheet
        open={Boolean(entryDraft)}
        onOpenChange={(next) => {
          if (!next) setEntryDraft(null);
        }}
        draft={entryDraft}
        use12HourFormat={isDay12HourFormat}
      />
    </section>
  );
}

export default Calendar;
